import {
  LitElement,
  html,
  css,
  nothing,
  type PropertyValues,
  type TemplateResult,
} from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { tokensStyles } from './styles/tokens.js';
import { createAuthClient, type AuthClient } from './auth-client.js';
import {
  AssistantApiError,
  DEFAULT_END_ENDPOINT,
  DEFAULT_FEEDBACK_ENDPOINT,
  DEFAULT_HISTORY_ENDPOINT,
  DEFAULT_MESSAGE_ENDPOINT,
  DEFAULT_MESSAGE_STREAM_ENDPOINT,
  DEFAULT_SESSION_ENDPOINT,
  createAssistantApiClient,
  type AssistantApiClient,
  type AssistantAction,
  type FeedbackFollowUp,
  type FeedbackPayload,
  type FeedbackReason,
  type FollowUpPromptAction,
  type LinkOutAction,
} from './api-client.js';
import type {
  MwiAssistantFeedbackDismissEventDetail,
  MwiAssistantFeedbackReasonsEventDetail,
} from './feedback-reason-card.js';
import { renderAssistantMarkdownBody } from './markdown.js';
import {
  isDeclineOfReturnQuestion,
  parseConversationEndMessage,
} from './conversation-end.js';

/**
 * Named theme tokens. Reserved for future variants. The default theme is
 * `mwi-default`. Theming surface is exposed via CSS custom properties on the
 * `:host` (see {@link tokensStyles}); the `theme` attribute lets the host opt
 * into pre-bundled variants once they exist.
 */
export type MwiAssistantTheme = 'mwi-default';

/**
 * Welcome-bubble starter chip. Static today; future task P1-067
 * (server-driven prompts) will derive these from
 * `AssistantSession.initialActions`.
 *
 * Per REQUIREMENTS.md §4.2 these chips are *one-tap user messages*.
 * Wired up in P1-052 via {@link MwiAssistantElement._onStarterPromptClick}.
 *
 *  - **Hidden prompt** (`hidden: true`): the chip shows {@link label} but
 *    the API receives {@link prompt}; the user bubble also shows
 *    {@link label}.
 *  - **Pre-defined prompt** (`hidden: false`): the chip shows
 *    {@link label} but the user bubble and API both use
 *    {@link prompt}.
 */
interface StarterPromptChip {
  readonly label: string;
  readonly prompt: string;
  readonly hidden: boolean;
}

/** Default starter chips shown in the welcome bubble. */
const DEFAULT_STARTER_PROMPTS: readonly StarterPromptChip[] = [
  {
    label: 'Recent Order Details',
    prompt:
      'Show me my 5 most recent orders. Show the order number, order date, PO, products ordered, and order quantity. Denote any items that are backordered.',
    hidden: true,
  },
  {
    label: 'Track Recent Shipments',
    prompt: 'Track my 5 most recent orders.',
    hidden: true,
  },
  {
    label: 'Return an Item',
    prompt: 'return an item',
    hidden: true,
  },
  {
    label: 'Update Address',
    prompt: 'How do I update my billing and/or shipping address?',
    hidden: false,
  },
  {
    label: 'Update License',
    prompt: 'How do I update my license and/or credentials?',
    hidden: false,
  },
];

/** Figma Chatbot_min / default / max total frame heights (see tokens.ts). */
const WIDGET_FRAME_HEIGHT_MIN_PX = 412;
const WIDGET_FRAME_HEIGHT_DEFAULT_PX = 600;
const WIDGET_FRAME_HEIGHT_MAX_PX = 740;

/**
 * Placeholder copy for the legal disclaimer modal (P1-051). This is NOT
 * the final wording — it must be replaced with text approved by MWI Legal
 * before any external (UAT or production) deployment. The host can override
 * it via the `disclaimer-text` attribute today; once Legal supplies the
 * canonical copy we will move it into a config-driven default.
 *
 * TODO(P1-051 / Legal review): replace with approved disclaimer copy.
 */
const DEFAULT_DISCLAIMER_TEXT =
  "This is an AI-powered assistant. Responses are AI-generated and may " +
  "contain errors — please verify important details with your account " +
  "representative. Conversations may be logged for quality and compliance " +
  "purposes. This assistant does not provide veterinary, medical, or " +
  "regulatory advice. By continuing, you accept these conditions.";

/**
 * `sessionStorage` key tracking whether the user has accepted the disclaimer
 * in the current browser tab/session. Versioned (`:v1`) so that if the
 * legal copy materially changes we can bump the suffix and force every user
 * to re-accept without breaking older tabs that are still reading the prior
 * key.
 */
const DISCLAIMER_STORAGE_KEY = 'mwi-assistant:disclaimer-accepted:v1';

/** Lit attribute converter: absent attr ⇒ true; `"false"` / `"0"` ⇒ false. */
const TRUTHY_DEFAULT_BOOLEAN_CONVERTER = {
  fromAttribute(value: string | null): boolean {
    if (value === null) {
      return true;
    }
    const v = value.toLowerCase();
    return v !== 'false' && v !== '0';
  },
  toAttribute(value: boolean): string | null {
    return value ? null : 'false';
  },
};

/** Lit attribute converter: absent attr ⇒ false; `"true"` / `"1"` / `""` ⇒ true. */
const FALSY_DEFAULT_BOOLEAN_CONVERTER = {
  fromAttribute(value: string | null): boolean {
    if (value === null) {
      return false;
    }
    const v = value.toLowerCase();
    if (v === 'false' || v === '0') {
      return false;
    }
    return true;
  },
  toAttribute(value: boolean): string | null {
    return value ? 'true' : null;
  },
};

/**
 * Custom event dispatched once the user accepts the disclaimer. Bubbles +
 * composed so the host page (Sitecore CD or otherwise) can listen at any
 * level — useful for analytics, audit logging, and triggering the actual
 * conversation flow once it lands in P1-052..P1-058.
 */
export const DISCLAIMER_ACCEPTED_EVENT = 'mwi-assistant:disclaimer-accepted';

/**
 * Visibility state machine for the widget shell.
 *
 *  - `expanded`  — full chat frame is shown (default first paint unless
 *                  the host sets `auto-open="false"`).
 *  - `minimized` — frame is hidden behind the launcher, **session
 *                  preserved**: transcript + disclaimer acceptance survive
 *                  re-open. Matches REQUIREMENTS.md §4.4 "minimize
 *                  preserves session".
 *  - `closed`    — frame is hidden behind the launcher, **session ended**:
 *                  transcript is cleared on next open. Matches
 *                  REQUIREMENTS.md §4.4 "close ends session — conversation
 *                  history is NOT saved after session end". Disclaimer
 *                  acceptance is intentionally *not* reset — the legal
 *                  acceptance is per browser session, not per chat session.
 *
 * The {@link MWI_ASSISTANT_OPENED_EVENT} / {@link MWI_ASSISTANT_MINIMIZED_EVENT}
 * / {@link MWI_ASSISTANT_CLOSED_EVENT} events fire on every transition so
 * the host page (analytics, nav-bar pulse animation in P4-007) can react.
 */
export type MwiAssistantVisibility = 'expanded' | 'minimized' | 'closed';

/**
 * Custom events for the visibility state machine. Names match the contract
 * documented in `planning/WIDGET-FRAMEWORK-CHOICE.md` §4 and bubble out of
 * the shadow root with `composed: true` so any host listener — including
 * a Vue 2 `@mwi-assistant-closed="…"` template binding on the Sitecore
 * side — picks them up without piercing shadow DOM.
 *
 * Detail payloads carry `{ conversationId: string | null }`. The
 * conversation id is `null` until the real session round-trip lands in
 * P1-057 / P1-058; once it does, this widget will populate the field
 * from `AssistantSession.conversationId` without a contract change.
 *
 * `MWI_ASSISTANT_CLOSED_EVENT` fires only after the user **confirms** the
 * close in the P1-056 confirmation modal. Cancelling the confirmation
 * leaves the widget expanded and dispatches no event — the host should
 * never observe a "false" close.
 */
export const MWI_ASSISTANT_OPENED_EVENT = 'mwi-assistant-opened';
export const MWI_ASSISTANT_MINIMIZED_EVENT = 'mwi-assistant-minimized';
export const MWI_ASSISTANT_CLOSED_EVENT = 'mwi-assistant-closed';

/**
 * Imperative commands the host app can broadcast (e.g. from Vue) on
 * `window` so the widget opens or hides the chat frame without using the
 * in-widget controls. Each mounted `<mwi-assistant>` listens and reacts;
 * use {@link MwiAssistantElement.assistantInstanceId} + event `detail` if
 * you need to target one instance when several are on the page.
 *
 * @example
 * window.dispatchEvent(new CustomEvent(MWI_ASSISTANT_REQUEST_OPEN));
 * window.dispatchEvent(new CustomEvent(MWI_ASSISTANT_REQUEST_MINIMIZE));
 */
export const MWI_ASSISTANT_REQUEST_OPEN = 'mwi-assistant:request-open';
export const MWI_ASSISTANT_REQUEST_MINIMIZE = 'mwi-assistant:request-minimize';

/** Optional `detail` on {@link MWI_ASSISTANT_REQUEST_OPEN} / {@link MWI_ASSISTANT_REQUEST_MINIMIZE}. */
export type MwiAssistantRequestEventDetail = {
  assistantInstanceId?: string;
};

/**
 * Per-turn feedback submission event (P3-023a + P3-023b-1). Dispatched
 * once a user's per-turn feedback intent has resolved. Bubbles +
 * composed so a host listener at any level (Sitecore CD, top-of-page
 * analytics, etc.) picks it up without piercing the shadow root —
 * same convention every other `mwi-assistant-*` event in this file
 * uses.
 *
 * Detail payload — see {@link MwiAssistantFeedbackEventDetail}:
 *
 *   { turnId, sentiment, kind: 'per-turn',
 *     followUp?, reasons? }
 *
 * Dispatch timing depends on `sentiment`:
 *
 *  - **Positive** thumb click → fires immediately. `followUp` and
 *    `reasons` are absent on positive payloads (the contract has no
 *    positive-reasons growth path today — see API-CONTRACT.md §5.5).
 *  - **Negative** thumb click → defers until the negative-reason
 *    follow-up resolves (P3-023b-1). The thumb-down click mounts the
 *    `<mwi-feedback-reason-card>` (P3-025a) inline beneath the
 *    feedback row; one of three resolution paths fires the event:
 *
 *      * card Submit (>=1 chip selected) →
 *        `followUp: 'reasons'`, `reasons: [...]` in render order
 *      * card Dismiss / Esc →
 *        `followUp: 'dismissed'` (no `reasons`)
 *      * confirmed close while card is still mounted →
 *        `followUp: 'abandoned'` (no `reasons`); auto-flushed by
 *        {@link MwiAssistantElement._endSession} BEFORE the
 *        transcript wipe so the signal isn't lost
 *
 *    This guarantees every thumbs-down click ultimately produces
 *    exactly one event with a resolved `followUp` — the contract
 *    invariant the server side (P3-022b) and the API integration
 *    (P3-023b-2) both lean on.
 *
 * The `kind` discriminator is forward-compat with the
 * end-of-chat overall-experience prompt (P3-024a) which will dispatch
 * the same event with `kind: 'overall'` so a single host listener can
 * route both signals through one analytics pipeline.
 *
 * Wired to `POST /api/feedback` since P3-023b-2 — every dispatch
 * also fires a fire-and-forget `submitFeedback` call through the
 * widget's api-client, with the event detail mapped 1:1 onto the
 * `FeedbackPayload` wire shape (see `API-CONTRACT.md` §5.5
 * + §5.5.4 + §5.5.5). The HTTP call is silent log-and-drop on
 * failure — see {@link MwiAssistantElement._dispatchFeedbackSubmit}
 * for the error / close-race policies.
 */
export const MWI_ASSISTANT_FEEDBACK_SUBMIT_EVENT =
  'mwi-assistant-feedback-submit';

/**
 * Shape of the `detail` payload on every {@link MWI_ASSISTANT_FEEDBACK_SUBMIT_EVENT}.
 *
 *  - `turnId` matches the `id` on the originating {@link MwiAssistantTurn}.
 *  - `sentiment` reflects which thumb was clicked.
 *  - `kind` is the source primitive — `'per-turn'` for the inline row,
 *    `'overall'` reserved for the P3-024a end-of-chat prompt.
 *  - `followUp` (P3-023b-1) is **present on negative dispatches only**
 *    and captures the three terminal resolutions of the negative
 *    follow-up flow (`'reasons'` / `'dismissed'` / `'abandoned'`). On
 *    positive dispatches the field is absent — there is no
 *    positive-reasons follow-up surface today (API-CONTRACT.md §5.5).
 *  - `reasons` (P3-023b-1) is **present only when `followUp === 'reasons'`**
 *    — the user's selected reason chips, in render order
 *    (`inaccurate, irrelevant, repetitive, unhelpful`). Absent on
 *    `'dismissed'` / `'abandoned'` (and on positives) so a host
 *    listener can pattern-match the field's presence as a proxy for
 *    "did the user elaborate".
 *
 * The optional fields are typed against the wire contract in
 * {@link FeedbackFollowUp} / {@link FeedbackReason} so a host listener
 * routing the event to `POST /api/feedback` (P3-023b-2) can map them
 * 1:1 onto the request body without further translation.
 */
export interface MwiAssistantFeedbackEventDetail {
  /** Absent on `kind: 'overall'` — end-of-chat feedback is session-scoped. */
  turnId?: string;
  sentiment: 'positive' | 'negative';
  kind: 'per-turn' | 'overall';
  followUp?: FeedbackFollowUp;
  reasons?: readonly FeedbackReason[];
}

/**
 * Initial copy on the per-turn feedback prompt (P3-023a). Matches the
 * Figma `Chatbot_postive feedback on response` reference frame
 * (`5642:4530`) — pre-click state. Provisional copy pending Liana's
 * review at P4-001.
 */
const FEEDBACK_PROMPT_INITIAL = 'Helpful response?';

/**
 * Post-click copy on the per-turn feedback prompt (P3-023a). Matches
 * the Figma `Chatbot_postive feedback on response` reference frame
 * (`5642:4454`) — sticky-once-clicked state. Both thumbs go inert at
 * this point.
 */
const FEEDBACK_PROMPT_SUBMITTED = 'Thanks for the feedback!';

/** Matches {@link AssistantClosureActions.DoneFollowUpId} on the wire. */
const CLOSURE_DONE_FOLLOW_UP_ID = 'closure-done';

/**
 * Wire message for the **"No, I'm done"** chip. Azure OpenAI's
 * self-harm filter treats the UI copy as a crisis phrase, so the
 * transcript keeps the Figma label while the `/message` body sends
 * this instead.
 */
const CLOSURE_DONE_BACKEND_MESSAGE = 'End Conversation';

/**
 * Correlation id passed to `<mwi-feedback-reason-card turn-id=…>` for
 * the end-of-chat negative flow so reason/dismiss events route to the
 * overall-feedback handlers instead of a transcript turn.
 */
const OVERALL_FEEDBACK_CORRELATION_ID = '__overall__';

/** Overall-experience prompt (P3-024a). Figma closure / feedback frames. */
const OVERALL_FEEDBACK_TITLE = 'Help us improve Ask MWI!';
const OVERALL_FEEDBACK_PROMPT = 'How was your experience overall?';

/** End-of-chat negative reason card title (P3-024a / TOKENS.md). */
const OVERALL_FEEDBACK_REASON_TITLE =
  'We are sorry that we missed the mark. Please select from the ' +
  'following options to help us understand why Ask MWI ' +
  'did not meet your expectations.';

/**
 * Default copy for the close-confirmation modal (P1-056). The wording is
 * provisional and will be reconciled against Liana's Figma during the
 * P4-001 Design QA gate, but the *behaviour* it gates — preventing
 * accidental session end per [REQUIREMENTS.md](planning/REQUIREMENTS.md)
 * §4.4 "Confirm on close" — is final.
 */
const CLOSE_CONFIRM_TITLE = 'End this conversation?';
const CLOSE_CONFIRM_BODY =
  'Closing will end your session. Your conversation history will not ' +
  'be saved. To keep this conversation available, minimize the chat ' +
  'instead.';
const CLOSE_CONFIRM_CONFIRM_LABEL = 'End conversation';
const CLOSE_CONFIRM_CANCEL_LABEL = 'Cancel';

/** Copy for the post–end-of-chat feedback thank-you overlay (P3-024c). */
const FEEDBACK_THANK_YOU_TITLE = 'Thank you';

/** Auto-dismiss delay before the thank-you overlay closes the widget. */
const FEEDBACK_THANK_YOU_AUTO_DISMISS_MS = 1_500;

/**
 * Shape of the `detail` payload on every visibility-state custom event.
 * Single shared shape keeps host-side listeners trivial. `conversationId`
 * is nullable until P1-057 / P1-058 land the session round-trip — see
 * the doc comment on the event constants.
 */
export interface MwiAssistantVisibilityEventDetail {
  conversationId: string | null;
}

/**
 * Delay (ms) before the thinking indicator swaps from animated dots to
 * a random playful line from {@link THINKING_TEXT_MESSAGES}. Per
 * REQUIREMENTS.md
 * §5.2: dots < 3 s, text ≥ 3 s. This is the spec value, not a tuning
 * knob — bake it in.
 */
const THINKING_TEXT_DELAY_MS = 3_000;

/**
 * Pool of playful clinic-themed lines shown once the thinking indicator
 * swaps from dots to text (REQUIREMENTS.md §5.2). A fresh line is chosen
 * at random on every {@link MwiAssistantElement._startThinking} call.
 * Final wording is provisional pending Liana's review at the P4-001
 * Design QA gate.
 */
export const THINKING_TEXT_MESSAGES = [
  'Looking up your answer',
  'Sniffing out the best answer for you',
  'Consulting the resident clinic cats',
  'Retrieving treats—I mean facts',
  'Your chart is in very good paws',
  'Chasing the tail of this question',
  'Warming up the stethoscope of wisdom',
  'Asking the therapy dog for a second opinion',
  'Purr-processing your request',
  'Holding still for the X-ray of insight',
  'Following the scent trail to an answer',
  'Gentle tail wags—almost there',
  'Checking under the couch for extra context',
  'Microchipping this thought with care',
  'The clinic hamster is on the wheel of computation',
  'Waiting for the whiteboard doodles to dry',
  'Wagging politely at your patience',
  'No fleas on this logic—just polishing',
  'Bribing the parrot with millet for gossip',
  'Tucking this idea in for a cozy nap',
  'Herding these words into a neat little flock',
] as const;

/** Pick one thinking-phase line from {@link THINKING_TEXT_MESSAGES}. */
export function pickThinkingText(): string {
  const index = Math.floor(Math.random() * THINKING_TEXT_MESSAGES.length);
  return THINKING_TEXT_MESSAGES[index] ?? THINKING_TEXT_MESSAGES[0];
}

/**
 * Returns `true` when a chat-history API payload carries no prior
 * messages. Unknown shapes are treated as empty so a malformed response
 * still falls back to the welcome bubble.
 */
export function isChatHistoryEmpty(history: unknown): boolean {
  return readChatHistoryEntries(history).length === 0;
}

/**
 * Transcript restored from a chat-history payload, plus whether that
 * history still ends on a conversation-end turn.
 */
export interface ParsedChatHistory {
  turns: MwiAssistantTurn[];
  /**
   * The newest history entry closed the chat (`conversation-end`).
   * A later user or assistant message clears this — the customer
   * kept the session going. Marker-only entries contribute no turn
   * (they have no customer-facing prose) but still set the flag
   * when they are the newest entry.
   */
  conversationEnded: boolean;
}

/**
 * Map a chat-history API payload onto {@link MwiAssistantTurn} rows for
 * transcript hydration. Accepts the live wire shape (`messages[]` with
 * `content`) and the legacy test/BFF shape (`turns[]` with `message`).
 * Entries with unknown roles or empty bodies are skipped.
 *
 * Assistant text is run through {@link parseConversationEndMessage}.
 * The storefront history feed can still carry the raw
 * `conversation-end` token (WR3NCH-4022): the live reply strips it
 * and shows overall feedback, but a later page load replays whatever
 * the BFF stored. Stripping here keeps that token off the transcript
 * after minimize / navigation.
 */
export function parseChatHistory(
  history: unknown,
  createTurnId: () => string
): ParsedChatHistory {
  const turns: MwiAssistantTurn[] = [];
  let conversationEnded = false;
  let previousAssistantText: string | undefined;
  let previousUserText: string | undefined;
  for (const entry of readChatHistoryEntries(history)) {
    const parsed = parseChatHistoryEntry(
      entry,
      createTurnId,
      previousUserText,
      previousAssistantText
    );
    if (parsed === null) {
      continue;
    }
    if (parsed.turn !== null) {
      turns.push(parsed.turn);
      if (parsed.turn.role === 'user') {
        previousUserText = parsed.turn.text;
      } else {
        previousAssistantText = parsed.turn.text;
      }
    }
    conversationEnded = parsed.closesConversation;
  }
  return { turns, conversationEnded };
}

/**
 * Turn rows from {@link parseChatHistory}. Callers that also need to
 * restore the end-of-chat card should use {@link parseChatHistory}.
 */
export function parseChatHistoryToTurns(
  history: unknown,
  createTurnId: () => string
): MwiAssistantTurn[] {
  return parseChatHistory(history, createTurnId).turns;
}

function readChatHistoryEntries(history: unknown): readonly unknown[] {
  if (history === null || typeof history !== 'object') {
    return [];
  }
  const obj = history as Record<string, unknown>;
  if (Array.isArray(obj.messages)) {
    return obj.messages;
  }
  if (Array.isArray(obj.turns)) {
    return obj.turns;
  }
  return [];
}

interface ParsedChatHistoryEntry {
  /** Null when the entry is a marker-only assistant turn. */
  turn: MwiAssistantTurn | null;
  /** This entry itself closed the chat. The caller tracks the newest. */
  closesConversation: boolean;
}

function parseChatHistoryEntry(
  entry: unknown,
  createTurnId: () => string,
  previousUserText: string | undefined,
  previousAssistantText: string | undefined
): ParsedChatHistoryEntry | null {
  if (entry === null || typeof entry !== 'object') {
    return null;
  }
  const obj = entry as Record<string, unknown>;
  const role = obj.role;
  if (role !== 'user' && role !== 'assistant') {
    return null;
  }
  let text = readChatHistoryMessageText(obj);
  let closesConversation = false;
  if (role === 'assistant') {
    const parsed = parseConversationEndMessage(text);
    if (parsed.isConversationEnd) {
      closesConversation = !isDeclineOfReturnQuestion(
        previousUserText,
        previousAssistantText
      );
      text = parsed.displayText.trim();
    }
  }
  if (text.length === 0) {
    return closesConversation
      ? { turn: null, closesConversation: true }
      : null;
  }
  return {
    turn: {
      id: createTurnId(),
      role,
      text,
      timestamp: parseChatHistoryTimestamp(obj.timestamp),
      ...(closesConversation ? { skipPerTurnFeedback: true } : {}),
    },
    closesConversation,
  };
}

function readChatHistoryMessageText(obj: Record<string, unknown>): string {
  if (typeof obj.content === 'string') {
    return obj.content.trim();
  }
  if (typeof obj.message === 'string') {
    return obj.message.trim();
  }
  return '';
}

function parseChatHistoryTimestamp(raw: unknown): Date {
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return new Date();
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() <= 1) {
    return new Date();
  }
  return parsed;
}

/**
 * Visually-hidden announcement read aloud when the thinking indicator
 * is in the dots phase. Without this, a screen-reader user gets nothing
 * announced for the first 3 s after submit (the dots themselves are
 * `aria-hidden`). The polite live region on `[part="body"]` picks this
 * up the moment the thinking turn is appended.
 */
const THINKING_DOTS_SR_LABEL = 'Assistant is thinking';

/**
 * Visually-hidden announcement read aloud while the session + history
 * bootstrap request is in flight.
 */
const BOOTSTRAP_LOADING_SR_LABEL = 'Loading conversation';

/**
 * Default user-facing copy shown in an error bubble when an SSE
 * stream surfaces no server-supplied message. The api-client
 * normally lifts the server's `error` / `message` fields verbatim
 * (see `./api-client.ts` `translateHttpError`); this fallback is
 * for the rare case where neither lands. Provisional copy + visual
 * treatment, flagged for Liana's review at the P4-001 Design QA
 * gate — REQUIREMENTS.md §5 does not yet spec error states.
 */
const DEFAULT_STREAM_ERROR_MESSAGE =
  'Something went wrong. Please try again.';

/**
 * Label on the Retry button rendered inside an error bubble.
 * Re-runs the originating user message through the same submission
 * pipeline as a fresh send — see {@link MwiAssistantElement._onRetryFromError}.
 * Provisional copy.
 */
const RETRY_BUTTON_LABEL = 'Try again';

/**
 * Conversation turn rendered in the transcript. Hydrated client-side
 * from streamed SSE `delta` frames (P1-058) via
 * {@link MwiAssistantElement._beginStreamingAssistantTurn} +
 * {@link MwiAssistantElement._appendDeltaToStreamingTurn} +
 * {@link MwiAssistantElement._completeStreamingTurn}.
 */
export interface MwiAssistantTurn {
  /** Stable, monotonically-unique within the session. */
  id: string;
  /** Who produced the turn. */
  role: 'user' | 'assistant';
  /** Body text. Markdown for assistant turns (rendered after streaming). */
  text: string;
  /** Wall-clock timestamp for the "Today at H:MM AM/PM" line. */
  timestamp: Date;
  /**
   * When set, this turn is a transient **thinking indicator** (P1-054)
   * rather than a regular message bubble. Always paired with
   * `role: 'assistant'`. Phase swaps from `'dots'` to `'text'` after
   * {@link THINKING_TEXT_DELAY_MS} per REQUIREMENTS.md §5.2.
   *
   * The thinking turn is replaced in-place by the streaming turn the
   * moment the first SSE `delta` arrives via
   * {@link MwiAssistantElement._beginStreamingAssistantTurn}.
   */
  thinking?: 'dots' | 'text';
  /**
   * Visible copy for the textual thinking phase. Set when the thinking
   * turn is created; one random line from {@link THINKING_TEXT_MESSAGES}
   * per {@link MwiAssistantElement._startThinking} invocation.
   */
  thinkingText?: string;
  /**
   * When `true`, this turn is currently being grown by streamed
   * `delta` frames (P1-058). The render path drops a thin visual
   * affordance (a trailing caret) so users can see the bubble is
   * still being typed; the flag is cleared by
   * {@link MwiAssistantElement._completeStreamingTurn} when the
   * `complete` SSE event lands.
   */
  streaming?: boolean;
  /**
   * Set when the SSE stream terminated in an error (either a
   * server-emitted `error` frame or a synthetic event the
   * api-client manufactured for a pre-stream HTTP failure /
   * transport drop). The render path swaps the bubble to the
   * error treatment + Retry button.
   *
   * `userText` carries the original user message that produced
   * this error so {@link MwiAssistantElement._onRetryFromError}
   * can re-submit it through the normal pipeline without the user
   * having to retype.
   *
   * `clientContext` preserves the optional context map that was
   * sent on the failing request — today only follow-up-prompt
   * chip clicks (P1-058c) populate it, with the originating
   * action's `parameters`. Retry feeds the same map back through
   * `_streamMessage` so a retried follow-up-prompt request keeps
   * its server-side hints.
   */
  error?: {
    readonly code: string;
    readonly message: string;
    readonly userText: string;
    readonly clientContext?: Readonly<Record<string, string>>;
  };
  /**
   * Suggested follow-up actions carried on the SSE `complete`
   * frame's `finalResponse.actions`. P1-058a renders `link-out`
   * actions; P1-058c renders `follow-up-prompt` actions. `intent`
   * actions are typed but unused on assistant bubbles today
   * (reserved for the welcome rail in P1-067).
   */
  actions?: readonly AssistantAction[];
  /**
   * Per-bubble one-shot gate for follow-up-prompt action chips
   * (P1-058c). Flips to `true` the moment any follow-up-prompt
   * chip in this bubble is clicked, and stays `true` for the
   * lifetime of the turn — meaning every follow-up-prompt chip
   * inside the same bubble disables on the first click. Other
   * turns' chips are unaffected. The flag is intentionally
   * scoped per-turn rather than globally because the welcome
   * starter chips already handle the "first user turn ends the
   * starter rail" semantic; follow-up-prompt suggestions are
   * per-turn alternatives, not a session-wide rail.
   *
   * `link-out` chips ignore this flag — their click semantic is
   * "open in new tab" and re-opening is harmless.
   */
  actionsConsumed?: boolean;
  /**
   * Per-turn feedback state (P3-023a). `undefined` while the
   * `Helpful response?` prompt + thumb-up/thumb-down primitive
   * is awaiting input; flips to `'positive'` or `'negative'`
   * the moment either thumb is clicked, and stays set for the
   * lifetime of the turn — both icons go inert and the prompt
   * swaps to "Thanks for the feedback!".
   *
   * Positive flips dispatch the
   * `mwi-assistant-feedback-submit` `CustomEvent` immediately.
   * Negative flips defer the dispatch until the follow-up
   * reason card resolves — see {@link feedbackPending}. P3-023b-2
   * will wire the eventual dispatch to `POST /api/feedback`.
   *
   * Never set on user turns or on the welcome turn — the
   * feedback row is gated to assistant content responses. See
   * {@link MwiAssistantElement._renderFeedbackRow}.
   */
  feedbackSubmitted?: 'positive' | 'negative';
  /**
   * Per-turn marker (P3-023b-1) for "user clicked thumbs-down,
   * the negative-reason follow-up card (P3-025a) is currently
   * mounted, and the `mwi-assistant-feedback-submit` event has
   * NOT yet been dispatched". Always paired with
   * `feedbackSubmitted === 'negative'`.
   *
   * Cleared by:
   *  - card Submit          → event fires with `followUp: 'reasons'`
   *  - card Dismiss / Esc   → event fires with `followUp: 'dismissed'`
   *  - confirmed close      → event fires with `followUp: 'abandoned'`
   *                           via {@link MwiAssistantElement._endSession}
   *                           BEFORE the transcript wipe so the
   *                           signal isn't lost
   *  - `disconnectedCallback` → cleared without dispatching (the
   *                             host page detached us; firing
   *                             synthetic events would be noise)
   *
   * Multiple turns may carry this flag concurrently — each card
   * is its own instance and each pending entry is per-turn.
   * There is no global "current card".
   */
  feedbackPending?: boolean;
  /**
   * When `true`, suppress the per-turn feedback row on this assistant
   * turn — used on the closure thank-you reply that carries the
   * session-scoped overall feedback card instead (P3-024a).
   */
  skipPerTurnFeedback?: boolean;
}

/**
 * MWI Ecomm Assistant widget.
 *
 * Embedding contract is documented in
 * `planning/WIDGET-FRAMEWORK-CHOICE.md` §4. In short:
 *
 * ```html
 * <mwi-assistant
 *   api-base="https://assistant.mwianimalhealth.com"
 *   token-endpoint="/api/assistant/token"
 *   disclaimer-text="..."   <!-- optional override -->
 *   min-height="480px"        <!-- optional frame floor (default 412px) -->
 *   theme="mwi-default">
 * </mwi-assistant>
 * ```
 *
 * Current scope (post-P1-052):
 *  - The static `Chatbot_default start` shell from Figma — header, welcome
 *    bubble, five starter chips, input row. Originally extracted from the
 *    Round 4 file; re-verified 2026-04-28 against the Dev-Handoff "Final
 *    Designs" file (key `DSMVDudtzt2Haob8uI37xQ`, section `5603:16105`).
 *  - A legal disclaimer modal (P1-051) overlays the chat on first paint
 *    and gates all interaction until the user accepts. Acceptance is
 *    persisted per session via `sessionStorage`. The provisional copy in
 *    {@link DEFAULT_DISCLAIMER_TEXT} must be replaced by approved Legal
 *    wording before any UAT/production deploy.
 *  - A working message input + transcript (P1-053): once the disclaimer
 *    is accepted the input + send button enable, Enter or click submits
 *    (Shift+Enter inserts a newline — see {@link _onInputKeydown}), a
 *    user bubble is appended to the transcript and a thinking indicator
 *    (P1-054) appears in its place. The indicator is replaced in-place
 *    by a streaming assistant bubble the moment the first SSE `delta`
 *    frame lands (P1-058) via {@link _beginStreamingAssistantTurn}.
 *  - A thinking / processing indicator (P1-054): each user submit
 *    appends a transient assistant turn that animates three dots for
 *    the first {@link THINKING_TEXT_DELAY_MS}ms, then swaps to the
 *    textual "Looking up your answer…" message per REQUIREMENTS.md
 *    §5.2. The indicator is removed automatically when the first SSE
 *    `delta` frame replaces it with a streaming bubble.
 *  - SSE-driven assistant replies (P1-058). On submit the widget
 *    awaits {@link _sessionReady} (initialised on disclaimer accept
 *    when auto-open, or on first open when host-controlled), POSTs
 *    `/api/assistant/message/stream`, and grows the
 *    trailing assistant bubble from streamed `delta` frames until a
 *    `complete` (final response, with optional `actions[]`) or
 *    `error` (Retry-able error bubble) terminal event lands. The
 *    api-client surface lives in `./api-client.ts`; transport-level
 *    SSE framing in `./sse-parser.ts`; both are independently
 *    unit-testable. Session lifecycle: `POST /api/assistant/session`
 *    on disclaimer-accept when `auto-open` (and on connect when
 *    sessionStorage shows acceptance), or on first {@link open} when
 *    `auto-open="false"`; `POST {end-endpoint}` with `{ id }`
 *    on confirmed close. Streams are aborted on confirmed-close and on
 *    `disconnectedCallback`, but **not** on minimize — minimize
 *    preserves the session per REQUIREMENTS.md §4.4.
 *  - One in-flight reply at a time. Send and Enter stay blocked until
 *    the request finishes or the user clicks Stop generating. The input
 *    stays editable so the next message can be drafted. Stop aborts the
 *    fetch (and the model call, when the server observes the
 *    disconnect). Text already on screen is kept as a finished
 *    assistant turn; if none has arrived, the thinking indicator is
 *    removed. The draft is left in the input.
 *  - One-tap starter chips (P1-052): the five welcome-bubble chips share
 *    the same submission path as the input — clicking a chip is
 *    identical to typing the chip label and pressing Enter, including
 *    the user bubble append and the {@link _startThinking} indicator
 *    handoff. The chip rail then disables permanently (because once a
 *    conversation has begun, starter prompts are no longer relevant).
 *    Defence-in-depth: chips are also disabled while the disclaimer
 *    modal is up.
 *  - Markdown assistant prose (P1-058d). While a turn is streaming,
 *    the bubble shows growing plain text + the streaming caret; once
 *    the `complete` frame lands, the body is parsed from markdown
 *    (GFM subset) into sanitized HTML with token-themed
 *    `.bubble__markdown` styles (lists, emphasis, inline links).
 *  - Inline action chips beneath the assistant bubble (P1-058a +
 *    P1-058c). The SSE `complete` frame's `finalResponse.actions`
 *    drives a chip row inside the same bubble: `link-out` chips
 *    are real `<a target="_blank">` links opening the supplied
 *    URL in a new tab; `follow-up-prompt` chips are
 *    `<button type="button">` controls that submit the chip's
 *    `label` as the next user message via
 *    {@link _onFollowUpPromptClick} — same submission pathway as
 *    a typed message or a starter chip. The chip's
 *    `parameters` map is forwarded as the `clientContext` field
 *    on the next `/message/stream` request, so server-side
 *    intent hints survive the click. Per-bubble click-once
 *    semantics: a follow-up-prompt click flips the originating
 *    turn's `actionsConsumed` flag and disables every chip in
 *    that bubble; chips on other bubbles are unaffected.
 *    `intent`-typed actions are reserved for the server-driven
 *    welcome rail (P1-067) and are silently skipped in-bubble.
 *  - Minimize / close header controls (P1-055) toggle visibility through
 *    a launcher button. Minimize preserves the session; close ends it
 *    per REQUIREMENTS.md §4.4.
 *  - A close-confirmation alertdialog (P1-056) gates the close path so
 *    a stray click on the X cannot silently destroy the transcript.
 *    Minimize is intentionally *not* gated — it is non-destructive.
 *
 *    The input is a `<textarea>` that auto-grows with content up to a
 *    4-line cap (`--mwi-assistant-size-input-max-height`, default
 *    `112px`) per REQUIREMENTS.md §5.1; past the cap the textarea
 *    scrolls internally. The placeholder swaps from "Type your question
 *    here…" to "Add your reply here…" the moment the first user turn
 *    lands.
 *
 * Tokens module (`./styles/tokens.js`) provides the `--mwi-assistant-*`
 * custom-property contract that both the chat surface and the disclaimer
 * modal consume — there are no hardcoded color/spacing/type literals in
 * the templates below, per `planning/BACKLOG.md` §1.6 hard rules.
 */
@customElement('mwi-assistant')
export class MwiAssistantElement extends LitElement {
  static override styles = [
    tokensStyles,
    css`
      :host {
        /* Position pinned to the bottom-right of the host viewport — this is
           the production-correct placement. Hosts can override by setting
           position/inset on the element directly. */
        display: block;
        position: fixed;
        bottom: var(--mwi-assistant-space-6);
        right: var(--mwi-assistant-space-6);
        width: var(--mwi-assistant-size-width);
        max-width: calc(100vw - 2 * var(--mwi-assistant-space-6));

        font-family: var(--mwi-assistant-font-family);
        color: var(--mwi-assistant-color-text-primary);
        z-index: 2147483000;
      }

      /* When collapsed (minimized or closed) the host shrinks to fit the
         circular launcher button — otherwise the 530px frame width would
         leave a wide invisible click-shield over the host page. The
         visibility attribute is reflected in updated() (see
         _reflectVisibilityAttribute). */
      :host([data-visibility='minimized']),
      :host([data-visibility='closed']) {
        width: auto;
      }

      /* Launcher hidden + collapsed: drop the host's layout footprint so
         the page is not covered by an invisible fixed-position box. */
      :host([data-launcher-visible='false'][data-visibility='minimized']),
      :host([data-launcher-visible='false'][data-visibility='closed']) {
        display: none;
      }

      /* === Frame ====================================================== */
      .frame {
        display: flex;
        flex-direction: column;
        position: relative;
        background: var(--mwi-assistant-color-bg-body);
        border-radius: var(--mwi-assistant-radius-md)
          var(--mwi-assistant-radius-md) 0 0;
        box-shadow: var(--mwi-assistant-shadow-overlay);
        overflow: clip;
        /* Default / user-resized height, capped at the Figma max and the
           viewport so bottom-anchored growth cannot push the header off-
           screen. The 100vh line is the fallback for browsers without dvh
           support. */
        height: min(
          var(--mwi-assistant-size-widget-height),
          calc(100vh - 2 * var(--mwi-assistant-space-6))
        );
        height: min(
          var(--mwi-assistant-size-widget-height),
          calc(100dvh - 2 * var(--mwi-assistant-space-6))
        );
        max-height: min(
          var(--mwi-assistant-size-widget-max-height),
          calc(100vh - 2 * var(--mwi-assistant-space-6))
        );
        max-height: min(
          var(--mwi-assistant-size-widget-max-height),
          calc(100dvh - 2 * var(--mwi-assistant-space-6))
        );
        /* Resizable floor (Figma Chatbot_min). Capped at the viewport so
           a tall min-height cannot push the header off-screen on short
           viewports (mirrors the max-height guard). */
        min-height: min(
          var(--mwi-assistant-size-widget-min-height),
          calc(100vh - 2 * var(--mwi-assistant-space-6))
        );
        min-height: min(
          var(--mwi-assistant-size-widget-min-height),
          calc(100dvh - 2 * var(--mwi-assistant-space-6))
        );
      }

      .frame__content {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        /* Figma frame: footer pinned to the bottom when the transcript
           is short; body absorbs remaining height when it is tall. */
        justify-content: space-between;
      }

      /* === Header ===================================================== */
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--mwi-assistant-space-2);
        padding: var(--mwi-assistant-space-3) var(--mwi-assistant-space-4);
        background: var(--mwi-assistant-color-bg-header);
        color: var(--mwi-assistant-color-text-on-primary);
        box-shadow: var(--mwi-assistant-shadow-header);
        position: relative;
        flex-shrink: 0;
      }

      .header__drag-handle {
        display: flex;
        align-items: center;
        flex: 1 1 auto;
        min-width: 0;
        align-self: stretch;
        /* Occupies the header row minus the action buttons — primary
           resize / reposition surface per Figma Dev Notes. */
        cursor: grab;
        touch-action: none;
        user-select: none;
      }

      .header__drag-handle:active {
        cursor: grabbing;
      }

      .header__brand {
        display: flex;
        align-items: center;
        gap: var(--mwi-assistant-space-4);
        min-width: 0;
      }

      .header__title {
        margin: 0;
        font-size: var(--mwi-assistant-font-heading-lg-size);
        line-height: var(--mwi-assistant-font-heading-lg-line);
        font-weight: var(--mwi-assistant-font-heading-lg-weight);
      }

      .header__actions {
        display: flex;
        align-items: center;
        gap: var(--mwi-assistant-space-4);
        flex-shrink: 0;
      }

      .header__icon-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: var(--mwi-assistant-space-1);
        border: 0;
        background: transparent;
        color: inherit;
        cursor: pointer;
        border-radius: var(--mwi-assistant-radius-sm);
      }

      .header__icon-button:hover {
        background: rgba(255, 255, 255, 0.12);
      }

      .header__icon-button:focus-visible {
        outline: 2px solid var(--mwi-assistant-color-text-on-primary);
        outline-offset: 2px;
      }

      .header__icon-button svg {
        display: block;
        width: var(--mwi-assistant-size-icon-md);
        height: var(--mwi-assistant-size-icon-md);
      }

      .header__avatar svg {
        display: block;
        width: var(--mwi-assistant-size-icon-md);
        height: var(--mwi-assistant-size-icon-md);
      }

      /* === Body ======================================================= */
      .body {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        padding: var(--mwi-assistant-space-6) var(--mwi-assistant-space-4);
        background: var(--mwi-assistant-color-bg-body);
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
      }

      /* Each rendered turn (welcome static block + every dynamic turn)
         is wrapped in a .turn so the timestamp sits with its bubble and
         consecutive turns get vertical breathing room. */
      .turn {
        display: flex;
        flex-direction: column;
      }

      .turn--user {
        align-items: flex-end;
      }

      .turn--assistant {
        align-items: flex-start;
      }

      .turn + .turn {
        margin-top: var(--mwi-assistant-space-4);
      }

      .timestamp {
        margin: 0 0 var(--mwi-assistant-space-2) 0;
        font-size: var(--mwi-assistant-font-body-2xs-size);
        line-height: var(--mwi-assistant-font-body-2xs-line);
        font-weight: var(--mwi-assistant-font-body-2xs-weight);
        color: var(--mwi-assistant-color-text-primary);
      }

      /* === Assistant message bubble =================================== */
      .bubble {
        display: flex;
        flex-direction: column;
        gap: var(--mwi-assistant-space-4);
        padding: var(--mwi-assistant-space-4);
        background: var(--mwi-assistant-color-bg-bubble-assistant);
        color: var(--mwi-assistant-color-text-primary);
        border-radius: var(--mwi-assistant-radius-md)
          var(--mwi-assistant-radius-md) var(--mwi-assistant-radius-md) 0;
        align-self: stretch;
      }

      /* Streaming-bubble caret (P1-058). While "delta" frames are
         still arriving the bubble grows token-by-token; a thin caret
         after the last character communicates "still typing" and
         disappears the moment the "complete" frame finalises the
         turn. Pure visual affordance — content is unchanged. */
      .bubble--streaming::after {
        content: '';
        display: inline-block;
        width: 2px;
        height: 1em;
        margin-left: 2px;
        vertical-align: -0.15em;
        background: currentColor;
        animation: mwi-assistant-streaming-caret-blink 1s steps(1)
          infinite;
      }

      @keyframes mwi-assistant-streaming-caret-blink {
        50% {
          opacity: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .bubble--streaming::after {
          animation: none;
        }
      }

      /* === Error bubble (P1-058) ====================================== */
      /* Provisional treatment + copy, flagged for Liana's review at
         the P4-001 Design QA gate. REQUIREMENTS.md §5 does not yet
         spec error states. Visual cue: muted-red background tint,
         primary text colour for legibility, plus an inline Retry
         button. */
      .bubble--error {
        background: var(--mwi-assistant-color-bg-bubble-error, #fdecec);
        color: var(--mwi-assistant-color-text-primary);
        gap: var(--mwi-assistant-space-3);
      }

      .error-retry {
        align-self: flex-start;
        padding: var(--mwi-assistant-space-2)
          var(--mwi-assistant-space-3);
        font-family: inherit;
        font-size: var(--mwi-assistant-font-button-sm-size);
        line-height: var(--mwi-assistant-font-button-sm-line);
        font-weight: var(--mwi-assistant-font-button-sm-weight);
        background: transparent;
        color: var(--mwi-assistant-color-interactive-primary);
        border: 1px solid var(--mwi-assistant-color-interactive-primary);
        border-radius: var(--mwi-assistant-radius-sm);
        cursor: pointer;
      }

      .error-retry:hover:not(:disabled) {
        background: var(--mwi-assistant-color-interactive-primary);
        color: var(--mwi-assistant-color-text-on-primary);
      }

      .error-retry:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .error-retry:focus-visible {
        outline: 2px solid
          var(--mwi-assistant-color-interactive-primary);
        outline-offset: 2px;
      }

      /* === Thinking indicator (P1-054) ================================ */
      /* The thinking turn reuses the assistant bubble shell so its
         alignment, padding, radius, and timestamp rhythm match a real
         response (one less visual jolt when the indicator is replaced).
         Phase 1 (dots) shows three pulsing dots; phase 2 (text) shows
         the textual "Looking up your answer…" line per REQUIREMENTS.md
         §5.2. Per the BACKLOG.md §1.6 hard rule, all colors / spacing
         reference custom-property tokens — the dot dimensions reuse the
         existing space scale rather than introducing a one-off literal. */
      .bubble--thinking {
        /* Thinking content is intentionally short — the bubble would
           otherwise stretch the full transcript width and read as a
           "real" reply. Cap to its content. */
        align-self: flex-start;
      }

      .thinking-dots {
        display: inline-flex;
        align-items: center;
        gap: var(--mwi-assistant-space-1);
      }

      /* Each dot pulses on a staggered loop so the row reads as the
         familiar "…" thinking animation. Reduced-motion users see a
         static row of dots — see the prefers-reduced-motion block
         below. */
      .thinking-dot {
        display: inline-block;
        width: var(--mwi-assistant-space-2);
        height: var(--mwi-assistant-space-2);
        border-radius: 50%;
        background: var(--mwi-assistant-color-text-muted);
        animation: mwi-assistant-thinking-pulse 1.4s infinite ease-in-out
          both;
      }

      .thinking-dot:nth-child(2) {
        animation-delay: 0.2s;
      }

      .thinking-dot:nth-child(3) {
        animation-delay: 0.4s;
      }

      @keyframes mwi-assistant-thinking-pulse {
        0%,
        80%,
        100% {
          transform: scale(0.6);
          opacity: 0.4;
        }
        40% {
          transform: scale(1);
          opacity: 1;
        }
      }

      /* Respect users who have requested reduced motion. The dots stay
         visible (so the "thinking" semantic is preserved) but they no
         longer animate — same accessibility treatment used by chat
         products like Slack and Teams. */
      @media (prefers-reduced-motion: reduce) {
        .thinking-dot {
          animation: none;
          opacity: 0.7;
        }
      }

      /* Phase 2 (text): playful line with the same pulsing dots as
         phase 1, aligned to the right of the copy. */
      .thinking-text {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: var(--mwi-assistant-space-2);
      }

      .thinking-text__label {
        flex: 1;
        min-width: 0;
      }

      .thinking-text .thinking-dots {
        flex-shrink: 0;
      }

      /* Visually-hidden text used to announce the dots phase to assistive
         tech. The dots themselves are aria-hidden, so without this the
         polite live region would have nothing to read for the first 3 s
         after submit. Standard sr-only / visually-hidden idiom. */
      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      /* === User message bubble ======================================== */
      /* Mirror treatment of the assistant bubble per TOKENS.md
         §"User message bubble" inference: pointer at bottom-right
         (radius 8 8 0 8), primary-tinted background, on-primary text.
         Provisional pending Liana's review during the P4-001 Design QA
         gate (Open question #4 in TOKENS.md). */
      .bubble--user {
        align-self: flex-end;
        max-width: 100%;
        padding: var(--mwi-assistant-space-4);
        background: var(--mwi-assistant-color-bg-bubble-user);
        color: var(--mwi-assistant-color-text-on-primary);
        border-radius: var(--mwi-assistant-radius-md)
          var(--mwi-assistant-radius-md) 0 var(--mwi-assistant-radius-md);
        word-break: break-word;
        white-space: pre-wrap;
      }

      .bubble__text-only {
        margin: 0;
        font-size: var(--mwi-assistant-font-body-sm-size);
        line-height: var(--mwi-assistant-font-body-sm-line);
        font-weight: var(--mwi-assistant-font-body-sm-weight);
      }

      /* Parsed assistant markdown (P1-058d). Scoped under the bubble
         so host-page CSS cannot pierce the Shadow DOM. */
      .bubble__markdown {
        margin: 0;
        font-size: var(--mwi-assistant-font-body-sm-size);
        line-height: var(--mwi-assistant-font-body-sm-line);
        font-weight: var(--mwi-assistant-font-body-sm-weight);
        word-break: break-word;
      }

      .bubble__markdown > :first-child {
        margin-top: 0;
      }

      .bubble__markdown > :last-child {
        margin-bottom: 0;
      }

      .bubble__markdown p {
        margin: 0;
      }

      .bubble__markdown p + p {
        margin-top: var(--mwi-assistant-font-paragraph-spacing);
      }

      .bubble__markdown ul,
      .bubble__markdown ol {
        margin: 0;
        padding-left: var(--mwi-assistant-space-6);
      }

      .bubble__markdown ul + p,
      .bubble__markdown ol + p,
      .bubble__markdown p + ul,
      .bubble__markdown p + ol {
        margin-top: var(--mwi-assistant-font-paragraph-spacing);
      }

      .bubble__markdown li + li {
        margin-top: var(--mwi-assistant-space-1);
      }

      .bubble__markdown strong,
      .bubble__markdown b {
        font-weight: var(--mwi-assistant-font-body-md-weight);
      }

      .bubble__markdown a {
        color: var(--mwi-assistant-color-interactive-primary);
        text-decoration: underline;
      }

      .bubble__markdown a:visited {
        color: var(--mwi-assistant-color-interactive-primary);
      }

      .bubble__markdown code {
        font-family: ui-monospace, 'Cascadia Code', 'Segoe UI Mono', monospace;
        font-size: 0.92em;
      }

      .bubble__heading {
        margin: 0 0 var(--mwi-assistant-space-1) 0;
        font-size: var(--mwi-assistant-font-body-md-size);
        line-height: var(--mwi-assistant-font-body-md-line);
        font-weight: var(--mwi-assistant-font-body-md-weight);
      }

      .bubble__paragraph {
        margin: 0;
        font-size: var(--mwi-assistant-font-body-sm-size);
        line-height: var(--mwi-assistant-font-body-sm-line);
        font-weight: var(--mwi-assistant-font-body-sm-weight);
      }

      .bubble__paragraph + .bubble__paragraph {
        margin-top: var(--mwi-assistant-font-paragraph-spacing);
      }

      /* === Starter chips ============================================== */
      .chips {
        display: flex;
        flex-wrap: wrap;
        gap: var(--mwi-assistant-space-2);
        margin: 0;
        padding: 0;
        list-style: none;
      }

      .chip {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        height: var(--mwi-assistant-size-button-height);
        padding: var(--mwi-assistant-space-1-5) var(--mwi-assistant-space-4);
        background: transparent;
        color: var(--mwi-assistant-color-interactive-primary);
        border: var(--mwi-assistant-border-thick) solid
          var(--mwi-assistant-color-interactive-primary);
        border-radius: var(--mwi-assistant-radius-sm);
        font-family: inherit;
        font-size: var(--mwi-assistant-font-button-sm-size);
        line-height: var(--mwi-assistant-font-button-sm-line);
        font-weight: var(--mwi-assistant-font-button-sm-weight);
        cursor: pointer;
        /* The class is shared between <button> (welcome-bubble
           starter chips, P1-052) and <a> (link-out action chips,
           P1-058a). The rules below neutralise user-agent <a>
           defaults so the two elements render identically.
           box-sizing: border-box is required because <button>
           inherits border-box from the UA stylesheet while <a>
           defaults to content-box — without it, link-out chips
           render ~12px taller than follow-up-prompt chips in
           the same row. */
        text-decoration: none;
        white-space: nowrap;
      }

      .chip,
      .chip:visited,
      .chip:hover,
      .chip:active {
        color: var(--mwi-assistant-color-interactive-primary);
      }

      .chip:hover:not(:disabled) {
        background: rgba(70, 30, 150, 0.06);
      }

      .chip:focus-visible {
        outline: 2px solid
          var(--mwi-assistant-color-interactive-primary);
        outline-offset: 2px;
      }

      /* Disabled state for chip button elements — applies to
         the welcome-bubble starter chips (P1-052, disabled
         after the first user turn) and to follow-up-prompt
         action chips (P1-058c, disabled after the first chip
         click in the same bubble). Visual treatment is
         intentionally minimal (faded + cursor change); a
         fuller treatment lands in the P4-001 Design QA gate
         once Liana's interaction-states extraction is
         consumed. link-out anchor chips never carry the
         disabled attribute, so this rule is button-only; the
         hover rule above gates on :not(:disabled) to keep the
         hover affordance off while disabled. */
      .chip:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      /* === Footer / input row ========================================= */
      .footer {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        padding: var(--mwi-assistant-space-6) var(--mwi-assistant-space-4);
        background: var(--mwi-assistant-color-bg-footer);
        flex-shrink: 0;
      }

      /* The input row grows with content (the textarea inside auto-resizes
         up to --mwi-assistant-size-input-max-height) so we use min-height
         rather than a fixed height — height comes from the textarea's
         intrinsic sizing plus our padding. align-items: center vertically
         centers the placeholder/text and the send button within the row so
         a single-line input doesn't leave dead whitespace above the
         children when the row sits at its min-height. As the textarea
         grows past one line the row grows with it and both children stay
         centered relative to the taller row; still matches
         REQUIREMENTS.md §5.1 ("field grows to fit text"). */
      .input-row {
        display: flex;
        align-items: center;
        gap: var(--mwi-assistant-space-1);
        width: 100%;
        min-height: var(--mwi-assistant-size-input-height);
        padding: var(--mwi-assistant-space-1-5)
          var(--mwi-assistant-space-4);
        background: var(--mwi-assistant-color-bg-input);
        border: var(--mwi-assistant-border-thin) solid
          var(--mwi-assistant-color-interactive-secondary);
        border-radius: var(--mwi-assistant-radius-sm);
        box-sizing: border-box;
      }

      .input-row:focus-within {
        border-color: var(--mwi-assistant-color-interactive-primary);
      }

      /* Used as the actual <form> element so Enter-to-submit works
         natively. width:100% so the row inside the column-aligned footer
         stretches the same way the legacy non-form row did. */
      .input-row--form {
        margin: 0;
      }

      /* The input is a <textarea> so it can auto-grow with multiline
         content per REQUIREMENTS.md §5.1. We disable manual resize
         (the user-drag handle) because the size is fully controlled by
         content + the max-height cap, and we let scroll kick in past
         the cap. The 'field-sizing: content' declaration below is the
         modern (Chromium 123+, Edge 123+) one-liner for content-driven
         sizing; for browsers without it, _autoResizeInput() does the
         same job in JS on every input event. */
      .input {
        flex: 1 1 auto;
        min-width: 0;
        border: 0;
        outline: 0;
        background: transparent;
        font-family: inherit;
        font-size: var(--mwi-assistant-font-body-sm-size);
        line-height: var(--mwi-assistant-font-body-sm-line);
        font-weight: var(--mwi-assistant-font-body-sm-weight);
        color: var(--mwi-assistant-color-text-primary);
        resize: none;
        overflow-y: auto;
        max-height: var(--mwi-assistant-size-input-max-height);
        /* Modern auto-grow; harmless on browsers that don't support it
           because _autoResizeInput() also runs in JS as a fallback. */
        field-sizing: content;
      }

      /* When content overflows the cap, surface a thin scrollbar that
         matches the rest of the surface so it doesn't visually jolt
         when the field reaches max-height. Webkit-only — Firefox uses
         scrollbar-color above. */
      .input::-webkit-scrollbar {
        width: 6px;
      }

      .input::-webkit-scrollbar-thumb {
        background: var(--mwi-assistant-color-interactive-secondary);
        border-radius: 3px;
      }

      .input::placeholder {
        color: var(--mwi-assistant-color-text-placeholder);
        font-style: italic;
      }

      .input:disabled {
        cursor: not-allowed;
      }

      .send,
      .stop {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: var(--mwi-assistant-size-button-height);
        padding: var(--mwi-assistant-space-1-5) var(--mwi-assistant-space-4);
        background: var(--mwi-assistant-color-interactive-primary);
        color: var(--mwi-assistant-color-text-on-primary);
        border: 0;
        border-radius: var(--mwi-assistant-radius-sm);
        cursor: pointer;
        flex-shrink: 0;
      }

      .send:hover:not(:disabled),
      .stop:hover:not(:disabled) {
        filter: brightness(1.08);
      }

      .send:disabled,
      .stop:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .send:focus-visible,
      .stop:focus-visible {
        outline: 2px solid
          var(--mwi-assistant-color-interactive-primary);
        outline-offset: 2px;
      }

      .send svg,
      .stop svg {
        display: block;
        width: var(--mwi-assistant-size-icon-sm);
        height: var(--mwi-assistant-size-icon-sm);
      }

      /* === Disclaimer modal (P1-051) ================================== */
      /* Overlays the entire frame until accepted. Uses the 'inert'
         attribute on .frame__content (set in render()) to make the chat
         non-interactive and skipped by assistive tech while the modal is
         showing. Visual treatment is provisional pending Liana's review
         during the P4-001 Design QA gate -- neither the Round 4 file nor
         the Dev-Handoff "Final Designs" file has an explicit 'disclaimer'
         state (re-verified 2026-04-28). */
      .disclaimer {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--mwi-assistant-space-4);
        background: var(--mwi-assistant-color-backdrop);
        border-radius: var(--mwi-assistant-radius-md)
          var(--mwi-assistant-radius-md) 0 0;
        box-sizing: border-box;
      }

      .disclaimer__card {
        display: flex;
        flex-direction: column;
        gap: var(--mwi-assistant-space-4);
        padding: var(--mwi-assistant-space-6);
        background: var(--mwi-assistant-color-bg-body);
        color: var(--mwi-assistant-color-text-primary);
        border-radius: var(--mwi-assistant-radius-md);
        box-shadow: var(--mwi-assistant-shadow-overlay);
        max-width: 100%;
        max-height: 100%;
        overflow-y: auto;
        box-sizing: border-box;
      }

      .disclaimer__title {
        margin: 0;
        font-size: var(--mwi-assistant-font-heading-lg-size);
        line-height: var(--mwi-assistant-font-heading-lg-line);
        font-weight: var(--mwi-assistant-font-heading-lg-weight);
      }

      .disclaimer__body {
        margin: 0;
        font-size: var(--mwi-assistant-font-body-sm-size);
        line-height: var(--mwi-assistant-font-body-sm-line);
        font-weight: var(--mwi-assistant-font-body-sm-weight);
        white-space: pre-line;
      }

      .disclaimer__actions {
        display: flex;
        justify-content: flex-end;
        gap: var(--mwi-assistant-space-2);
        margin-top: var(--mwi-assistant-space-2);
      }

      .disclaimer__accept {
        height: var(--mwi-assistant-size-button-height);
        padding: var(--mwi-assistant-space-1-5)
          var(--mwi-assistant-space-4);
        background: var(--mwi-assistant-color-interactive-primary);
        color: var(--mwi-assistant-color-text-on-primary);
        border: 0;
        border-radius: var(--mwi-assistant-radius-sm);
        font-family: inherit;
        font-size: var(--mwi-assistant-font-button-sm-size);
        line-height: var(--mwi-assistant-font-button-sm-line);
        font-weight: var(--mwi-assistant-font-button-sm-weight);
        cursor: pointer;
      }

      .disclaimer__accept:hover {
        filter: brightness(1.08);
      }

      .disclaimer__accept:focus-visible {
        outline: 2px solid
          var(--mwi-assistant-color-interactive-primary);
        outline-offset: 2px;
      }

      /* === Close confirmation modal (P1-056) ========================== */
      /* Same idiom as the disclaimer modal: absolutely positioned inside
         the frame, scrim backdrop, card with title/body/actions. The
         duplication of disclaimer__* / confirm__* styles is deliberate
         — extracting a shared .modal-* base would touch P1-051's tested
         markup (and surface area) for marginal payoff. If a third modal
         appears, extract then. Visual treatment is provisional pending
         Liana's review during the P4-001 Design QA gate. */
      .confirm {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--mwi-assistant-space-4);
        background: var(--mwi-assistant-color-backdrop);
        border-radius: var(--mwi-assistant-radius-md)
          var(--mwi-assistant-radius-md) 0 0;
        box-sizing: border-box;
      }

      .confirm__card {
        display: flex;
        flex-direction: column;
        gap: var(--mwi-assistant-space-4);
        padding: var(--mwi-assistant-space-6);
        background: var(--mwi-assistant-color-bg-body);
        color: var(--mwi-assistant-color-text-primary);
        border-radius: var(--mwi-assistant-radius-md);
        box-shadow: var(--mwi-assistant-shadow-overlay);
        max-width: 100%;
        max-height: 100%;
        overflow-y: auto;
        box-sizing: border-box;
      }

      .confirm__title {
        margin: 0;
        font-size: var(--mwi-assistant-font-heading-lg-size);
        line-height: var(--mwi-assistant-font-heading-lg-line);
        font-weight: var(--mwi-assistant-font-heading-lg-weight);
      }

      .confirm__body {
        margin: 0;
        font-size: var(--mwi-assistant-font-body-sm-size);
        line-height: var(--mwi-assistant-font-body-sm-line);
        font-weight: var(--mwi-assistant-font-body-sm-weight);
      }

      .confirm__actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: var(--mwi-assistant-space-2);
        margin-top: var(--mwi-assistant-space-2);
      }

      .confirm__button {
        height: var(--mwi-assistant-size-button-height);
        padding: var(--mwi-assistant-space-1-5)
          var(--mwi-assistant-space-4);
        border-radius: var(--mwi-assistant-radius-sm);
        font-family: inherit;
        font-size: var(--mwi-assistant-font-button-sm-size);
        line-height: var(--mwi-assistant-font-button-sm-line);
        font-weight: var(--mwi-assistant-font-button-sm-weight);
        cursor: pointer;
      }

      /* Secondary outlined button — same chip-style outline used on the
         starter prompts. This is the *default-focus* control: Cancel
         must visually read as the safe path so a user about to mash
         Enter on the modal does not destroy their session. */
      .confirm__button--secondary {
        background: transparent;
        color: var(--mwi-assistant-color-interactive-primary);
        border: var(--mwi-assistant-border-thick) solid
          var(--mwi-assistant-color-interactive-primary);
      }

      .confirm__button--secondary:hover {
        background: rgba(70, 30, 150, 0.06);
      }

      /* Primary filled button — same treatment as the disclaimer
         accept and the send button. Visually loud (purple fill) so
         the destructive action is unmistakably the destructive
         action. */
      .confirm__button--primary {
        background: var(--mwi-assistant-color-interactive-primary);
        color: var(--mwi-assistant-color-text-on-primary);
        border: 0;
      }

      .confirm__button--primary:hover {
        filter: brightness(1.08);
      }

      .confirm__button:focus-visible {
        outline: 2px solid
          var(--mwi-assistant-color-interactive-primary);
        outline-offset: 2px;
      }

      /* === Launcher (P1-055) ========================================== */
      /* Floating circular CTA shown when the widget is minimized or closed.
         Provisional treatment — the production launcher is owned by P4-006
         (nav-bar integration) and P4-007 (pulsing animation when a session
         is active). For Phase 1 the launcher lives inside the widget so
         minimize/close are reachable end-to-end without the Sitecore nav
         work landing first. The Figma launcher (a 156×32 cds-button in the
         host top nav, e.g. node 5642:1761 in the Dev-Handoff file) is
         tracked as Open question #8 in TOKENS.md. */
      .launcher {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: var(--mwi-assistant-size-launcher);
        height: var(--mwi-assistant-size-launcher);
        padding: 0;
        background: var(--mwi-assistant-color-interactive-primary);
        color: var(--mwi-assistant-color-text-on-primary);
        border: 0;
        border-radius: 50%;
        box-shadow: var(--mwi-assistant-shadow-overlay);
        cursor: pointer;
        font-family: inherit;
      }

      .launcher:hover {
        filter: brightness(1.08);
      }

      .launcher:focus-visible {
        outline: 2px solid
          var(--mwi-assistant-color-interactive-primary);
        outline-offset: 2px;
      }

      .launcher svg {
        display: block;
        width: var(--mwi-assistant-size-icon-md);
        height: var(--mwi-assistant-size-icon-md);
      }

      /* === Per-turn feedback row (P3-023a) ============================ */
      /* Inline 'Helpful response?' + thumb-up / thumb-down primitive
         beneath every assistant content turn (NOT the welcome turn).
         Sticky-once-clicked: prompt swaps to 'Thanks for the feedback!',
         the chosen icon flips to its filled treatment, and both buttons
         go inert. Layout matches the Figma 'Feeback' container (node
         5642:4668 in dev-handoff — note the 'Feeback' typo on Figma's
         side) — flex row, gap 24px, sitting OUTSIDE the bubble as a
         sibling separated by --mwi-assistant-space-4 (16px) above. The
         row's content width is intrinsic (it does NOT span the body
         width). See planning/figma/TOKENS.md "Per-turn feedback row
         (P3-023a)" for the canonical spec. */
      .feedback-row {
        display: flex;
        align-items: center;
        gap: var(--mwi-assistant-space-6);
        margin-top: var(--mwi-assistant-space-4);
      }

      .feedback-prompt {
        font-size: var(--mwi-assistant-font-body-2xs-size);
        line-height: var(--mwi-assistant-font-body-2xs-line);
        font-weight: var(--mwi-assistant-font-body-2xs-weight);
        color: var(--mwi-assistant-color-text-secondary);
      }

      /* The thumb buttons share one base block — identical sizing,
         padding, background, focus ring — so the only thing that varies
         between thumb-up and thumb-down at the production level is the
         SVG transform. Keeps the Figma "same primitive, mirrored" intent
         intact and means a future visual tweak (e.g. Liana adds a hover
         tint) only needs editing in one place. */
      .feedback-thumb {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        padding: 0;
        background: transparent;
        border: 0;
        color: var(--mwi-assistant-color-text-primary);
        cursor: pointer;
      }

      .feedback-thumb:focus-visible {
        outline: 2px solid
          var(--mwi-assistant-color-interactive-primary);
        outline-offset: 2px;
      }

      .feedback-thumb svg {
        display: block;
        width: 20px;
        height: 20px;
        /* Stroke is bound to currentColor so a host that retints
           the row's text color doesn't leave the icon stroke
           orphaned at the default neutral. */
        stroke: currentColor;
        fill: none;
      }

      /* Selected (pressed) thumb — flips the SVG to a filled-purple
         treatment per the Figma 'Chatbot_postive feedback on response'
         reference (5642:4454 / 5642:4530). Stroke matches fill so the
         outline doesn't leak through against the filled body. */
      .feedback-thumb[aria-pressed='true'] svg {
        fill: var(--mwi-assistant-color-interactive-primary);
        stroke: var(--mwi-assistant-color-interactive-primary);
      }

      /* Disabled but NOT pressed — i.e. the thumb the user did not
         click on a turn whose feedback has already been submitted.
         Faded + cursor-change so it visually reads as "no longer
         actionable" without being so washed out the icon shape is
         indistinguishable. */
      .feedback-thumb:disabled:not([aria-pressed='true']) {
        opacity: 0.5;
        cursor: not-allowed;
      }

      /* The pressed-and-disabled thumb keeps full opacity (it's the
         user's recorded answer; we don't want to fade their own
         click) but still swaps the cursor so it reads as inert. */
      .feedback-thumb:disabled[aria-pressed='true'] {
        cursor: default;
      }

      /* Reduced-motion users have nothing to opt out of today (the
         state swap is instantaneous, no transitions defined above)
         but we set transition: none defensively so a future visual
         tweak that adds a transition cannot bypass the
         accessibility gate. */
      @media (prefers-reduced-motion: reduce) {
        .feedback-thumb,
        .feedback-thumb svg {
          transition: none;
        }
      }

      /* === Overall feedback card (P3-024a) ============================ */
      .overall-feedback {
        display: flex;
        flex-direction: column;
        gap: var(--mwi-assistant-space-4);
        margin-top: var(--mwi-assistant-space-4);
        padding: var(--mwi-assistant-space-4);
        background: var(--mwi-assistant-color-bg-bubble-assistant);
        border-radius: var(--mwi-assistant-radius-md);
      }

      .overall-feedback__title {
        margin: 0;
        font-size: var(--mwi-assistant-font-body-sm-size);
        line-height: var(--mwi-assistant-font-body-sm-line);
        font-weight: 700;
        color: var(--mwi-assistant-color-text-primary);
      }

      .overall-feedback__row {
        display: flex;
        align-items: center;
        gap: var(--mwi-assistant-space-6);
      }
    `,
  ];

  /**
   * Base URL or path prefix for the Assistant API. Relative values
   * (e.g. `/api/mwi`) are resolved against the embedding page
   * origin; absolute `http(s)://` values target a remote host (e.g.
   * Azure Container Apps). Each `*-endpoint` attribute is joined to
   * this base via {@link resolveApiPath} unless the endpoint is
   * already prefixed or absolute.
   */
  @property({ type: String, attribute: 'api-base', reflect: true })
  apiBase = '';

  /**
   * URL of the host-side token broker that returns a JWT for the Assistant
   * API. Typically a Sitecore CD endpoint; see `planning/AUTHENTICATION.md`.
   * May be relative (e.g. `/api/assistant/token`) or absolute. When
   * {@link apiBase} is a root-relative path prefix, this value is joined
   * under that prefix (e.g. api-base="/api/mwi" +
   * token-endpoint="/chatassistant/token" → `/api/mwi/chatassistant/token`).
   * When {@link apiBase} is an absolute origin, this stays page-relative.
   */
  @property({ type: String, attribute: 'token-endpoint', reflect: true })
  tokenEndpoint = '';

  /**
   * Endpoint for minting a conversation (`POST`). Relative values are
   * appended to {@link apiBase}; absolute `http(s)://` URLs are used as-is.
   */
  @property({ type: String, attribute: 'session-endpoint', reflect: true })
  sessionEndpoint = DEFAULT_SESSION_ENDPOINT;

  /**
   * Endpoint for sending a user message and receiving the full assistant
   * reply as JSON (`POST`). Relative values are appended to
   * {@link apiBase}; absolute `http(s)://` URLs are used as-is.
   */
  @property({ type: String, attribute: 'message-endpoint', reflect: true })
  messageEndpoint = DEFAULT_MESSAGE_ENDPOINT;

  /**
   * Endpoint for streaming the assistant reply as SSE (`POST`). Relative
   * values are appended to {@link apiBase}; absolute `http(s)://` URLs
   * are used as-is.
   */
  @property({ type: String, attribute: 'message-stream-endpoint', reflect: true })
  messageStreamEndpoint = DEFAULT_MESSAGE_STREAM_ENDPOINT;

  /**
   * Endpoint for fetching persisted chat history after a session id is minted.
   * Relative values are appended to {@link apiBase}; absolute `http(s)://`
   * URLs are used as-is. The widget POSTs `{ id }` to this endpoint.
   * Set to an empty string to disable chat-history fetches.
   */
  @property({ type: String, attribute: 'history-endpoint', reflect: true })
  historyEndpoint = DEFAULT_HISTORY_ENDPOINT;

  /**
   * Endpoint for ending the conversation on confirmed close. Relative
   * values are appended to {@link apiBase}; absolute `http(s)://` URLs
   * are used as-is. The widget POSTs `{ id }` to this endpoint.
   * Set to an empty string to skip end-session calls.
   */
  @property({ type: String, attribute: 'end-endpoint', reflect: true })
  endEndpoint = DEFAULT_END_ENDPOINT;

  /**
   * Endpoint for submitting per-turn or end-of-chat feedback (`POST`).
   * Relative values are appended to {@link apiBase}; absolute `http(s)://`
   * URLs are used as-is.
   */
  @property({ type: String, attribute: 'feedback-endpoint', reflect: true })
  feedbackEndpoint = DEFAULT_FEEDBACK_ENDPOINT;

  /**
   * Named theme. Defaults to `mwi-default`. Reserved for future variants.
   */
  @property({ type: String, reflect: true })
  theme: MwiAssistantTheme = 'mwi-default';

  /**
   * When false, the floating launcher is not rendered while the widget is
   * minimized or closed. The chat frame is unchanged when expanded; use
   * {@link open}, {@link minimize}, or {@link MWI_ASSISTANT_REQUEST_OPEN} /
   * {@link MWI_ASSISTANT_REQUEST_MINIMIZE} to toggle visibility from the host.
   *
   * Reflected to `data-launcher-visible` on the host for styling.
   *
   * Shares the module's truthy-default boolean attribute converter with
   * {@link autoOpen} so `launcher-visible="false"` (and Vue
   * `:launcher-visible="false"`) maps to boolean false — Lit's default boolean
   * attribute rules treat any *present* attribute as true.
   */
  @property({
    attribute: 'launcher-visible',
    converter: TRUTHY_DEFAULT_BOOLEAN_CONVERTER,
  })
  launcherVisible = true;

  /**
   * When false, the widget mounts in the `minimized` state (launcher only)
   * instead of opening the chat frame immediately. Does not fire
   * `mwi-assistant-minimized` on mount — that event is reserved for an
   * actual transition from expanded. Uses the same attribute converter as
   * {@link launcherVisible}.
   */
  @property({
    attribute: 'auto-open',
    converter: TRUTHY_DEFAULT_BOOLEAN_CONVERTER,
  })
  autoOpen = true;

  /**
   * When true, outbound chat uses {@link messageStreamEndpoint} (SSE token
   * deltas). When false (default), uses {@link messageEndpoint} and shows
   * the assistant reply once the full JSON response returns.
   */
  @property({
    attribute: 'use-streaming',
    converter: FALSY_DEFAULT_BOOLEAN_CONVERTER,
  })
  useStreaming = false;

  /**
   * Optional id for {@link MWI_ASSISTANT_REQUEST_OPEN} and
   * {@link MWI_ASSISTANT_REQUEST_MINIMIZE} when `detail.assistantInstanceId`
   * is set — only the matching element handles the event.
   */
  @property({ type: String, attribute: 'assistant-instance-id' })
  assistantInstanceId = '';

  /**
   * Override copy for the legal disclaimer modal. Empty string falls back
   * to {@link DEFAULT_DISCLAIMER_TEXT}. Hosts may pass approved Legal
   * wording via this attribute today; once Legal supplies canonical
   * wording it will be promoted into the bundled default and this
   * attribute will become an *override* surface (e.g. for partner tenants).
   */
  @property({ type: String, attribute: 'disclaimer-text' })
  disclaimerText = '';

  /**
   * Minimum height of the expanded chat frame (CSS length, e.g. `412px`,
   * `50vh`). Maps to `--mwi-assistant-size-widget-min-height` on the host
   * and clamps user resize. When omitted, the bundled Figma floor (`412px`)
   * applies.
   */
  @property({ type: String, attribute: 'min-height', reflect: true })
  minHeight = '';

  /**
   * Whether the user has accepted the legal disclaimer in the current
   * browser session. Mirrored to `sessionStorage` (key
   * {@link DISCLAIMER_STORAGE_KEY}) so a page reload within the same tab
   * does not re-prompt; a fresh tab or browser restart does.
   */
  @state()
  private _disclaimerAccepted = false;

  /**
   * `true` while {@link _initSession} is fetching the conversation id
   * and chat history. Drives the bootstrap loading indicator (animated
   * dots) in the transcript until both requests settle.
   */
  @state()
  private _sessionBootstrapping = false;

  /**
   * Whether the static welcome bubble (heading + starter chips) is
   * visible. Starts `false`; set `true` only after session + history
   * bootstrap completes with an empty history payload.
   */
  @state()
  private _showWelcomeTurn = false;

  /**
   * Live transcript of dynamic conversation turns rendered after the
   * static welcome bubble. Append-only; turns are never mutated in place.
   * Today this is fully client-driven (P1-053). Once P1-058 lands, the
   * same array will be hydrated from streamed `delta` SSE frames via the
   * {@link _appendAssistantTurn} seam — no render-path changes needed.
   */
  @state()
  private _turns: MwiAssistantTurn[] = [];

  /**
   * Controlled value of the message input. Tracked in component state so
   * we can clear it after submit and so the send button's disabled state
   * can react to whitespace-only content. Bound one-way into the input
   * via `.value` and kept in sync via the `input` event.
   */
  @state()
  private _inputValue = '';

  /**
   * Counter feeding {@link _generateTurnId} as a fallback when
   * `crypto.randomUUID` is unavailable (older happy-dom builds, certain
   * non-browser test runners). Monotonic per element instance.
   */
  private _turnIdCounter = 0;

  /**
   * Visibility state machine — see {@link MwiAssistantVisibility}.
   *
   * Default `expanded` unless {@link autoOpen} is false (see
   * `connectedCallback`). The
   * minimize / close header buttons (P1-055) drive transitions to
   * `minimized` / `closed`; the launcher button drives transitions back
   * to `expanded`. Tracked as `@state` rather than `@property` because
   * it is purely internal — hosts observe transitions through the
   * documented custom events, not through attribute reads.
   *
   * Mirrored to the `data-visibility` attribute on the host element via
   * {@link _reflectVisibilityAttribute} so `:host([data-visibility=…])`
   * style rules can shrink the host to the launcher's footprint when
   * collapsed.
   */
  @state()
  private _visibility: MwiAssistantVisibility = 'expanded';

  /**
   * Whether the P1-056 close-confirmation alertdialog is currently open.
   *
   * Set true by {@link _onClose} (the header X click) and cleared by
   * either {@link _onCancelClose} (Cancel button / Escape key / scrim
   * click) or {@link _onConfirmClose} (the destructive "End conversation"
   * button). The chat surface beneath is marked `inert` while this is
   * true so the modal cannot be tabbed past or dismissed by clicking
   * through to the input.
   *
   * Tracked as `@state` rather than `@property` because it is a
   * transient interactive state that hosts have no business observing
   * directly — `mwi-assistant-closed` still fires only after an actual
   * confirmed close.
   */
  @state()
  private _closeConfirmOpen = false;

  /**
   * Non-interactive thank-you overlay shown briefly after overall
   * end-of-chat negative feedback is submitted (P3-024c). Cleared
   * automatically after {@link FEEDBACK_THANK_YOU_AUTO_DISMISS_MS},
   * then {@link _performClose} runs. The chat surface is `inert`
   * while this is true.
   */
  @state()
  private _feedbackThankYouOpen = false;

  /**
   * `setTimeout` handle for the thank-you auto-dismiss (P3-024c).
   * Cleared on {@link disconnectedCallback} and when close runs early.
   */
  private _feedbackThankYouTimer: number | null = null;

  /**
   * Active conversation id (P1-058). Populated by {@link _initSession}
   * — on disclaimer-accept / connect when `auto-open`, or on first
   * {@link open} when host-controlled (`auto-open="false"`). Carried in the
   * visibility custom-event detail payloads, on every
   * `/message/stream` request, and used as the path parameter for
   * `POST {end-endpoint}` with `{ id }` on confirmed close.
   *
   * `null` between widget mount and the moment `_initSession`
   * resolves; consumers race-safe via {@link _sessionReady}.
   */
  private _conversationId: string | null = null;

  /**
   * Id of the in-flight thinking turn (P1-054), or `null` when no
   * thinking indicator is currently in the transcript. We keep this
   * out of `_turns` derivation because the value is needed by the
   * timer callbacks (which fire outside the render cycle) and by
   * {@link _appendAssistantTurn} so it can replace the indicator
   * with the real reply.
   */
  private _thinkingTurnId: string | null = null;

  /**
   * `setTimeout` handle for the dots → text phase swap (P1-054). Stored
   * so we can cancel it if the user closes mid-thinking, submits a new
   * message, or the real reply lands before the swap fires. `number`
   * matches the browser type — Vite/TypeScript pick `Window.setTimeout`
   * here, not the Node overload.
   */
  private _thinkingPhaseTimer: number | null = null;

  /**
   * Lazily-instantiated auth-aware fetch helper (P1-057). Constructed
   * the first time {@link authedFetch} is called, and re-constructed
   * if the `tokenEndpoint` property changes between calls so that a
   * cached token issued for endpoint A is never reused after the host
   * page repoints to endpoint B (e.g. switching environments at
   * runtime). The full design lives in `./auth-client.ts`; this
   * field is just the per-element cache so the same closure-state
   * (cached token, in-flight broker request) is shared across all
   * widget-initiated calls within the element's lifetime.
   */
  private _authClient: AuthClient | null = null;

  /**
   * `tokenEndpoint` value the current `_authClient` was constructed
   * against. Tracked separately because the helper's closure only
   * captures the endpoint at construction time — comparing this on
   * every {@link authedFetch} call is what lets us drop a stale
   * client when the host repoints us.
   */
  private _authClientConfigKey: string | null = null;

  /**
   * Lazily-constructed Assistant API client (P1-058). Built the first
   * time {@link _getApiClient} runs — and rebuilt if `apiBase`
   * changes between calls so a stale base URL captured in the
   * client's closure can't outlive a host repoint. Caches nothing
   * itself; it's the api-client's wrapper around `authedFetch` that
   * needs per-element scoping.
   */
  private _apiClient: AssistantApiClient | null = null;

  /**
   * Serialized endpoint configuration the current `_apiClient` was
   * constructed against. Compared on every call so a stale client is
   * dropped when the host changes any endpoint attribute.
   */
  private _apiClientConfigKey: string | null = null;

  /**
   * Promise that resolves once {@link _initSession} has populated
   * {@link _conversationId}. Lets {@link _streamMessage} race-safely
   * await readiness when a user submits before the eagerly-kicked
   * `/session` POST has come back (fast typist on slow network).
   *
   * Set to a fresh promise on every `_initSession` invocation;
   * cleared back to `null` on confirmed-close so a re-open re-mints.
   */
  private _sessionReady: Promise<void> | null = null;

  /**
   * `AbortController` for the in-flight message request, or `null`
   * when none is open. Used by Stop generating, confirmed-close, and
   * `disconnectedCallback`. The api-client forwards the signal to
   * `fetch`, so the browser drops the connection and ASP.NET cancels
   * the model call.
   *
   * **Not** aborted on minimize — minimize preserves the session
   * per REQUIREMENTS.md §4.4, so a long-running response keeps
   * growing the bubble while the widget is collapsed.
   *
   * A second submit does **not** abort this controller. Only one
   * request may be in flight; further submits are ignored until it
   * finishes or the user stops it.
   */
  private _inflightStreamAbort: AbortController | null = null;

  /**
   * True while a message request is in flight. Drives the disabled
   * Send button and the Stop generating button. Set synchronously in
   * {@link _tryStartRequest} so a second click or Enter in the same
   * turn cannot start another request before Lit re-renders.
   */
  @state()
  private _generating = false;

  /**
   * Monotonic id of the request {@link _streamMessage} is allowed to
   * apply. Stop, close, and detach increment it so a response that
   * arrives after the user has moved on cannot grow or replace a turn.
   */
  private _activeGeneration = 0;

  /**
   * Id of the trailing assistant turn currently being grown by
   * `delta` frames, or `null` between streams. Set by
   * {@link _beginStreamingAssistantTurn} on the first delta;
   * cleared by {@link _completeStreamingTurn} or
   * {@link _errorStreamingTurn} on the terminal frame.
   */
  private _streamingTurnId: string | null = null;

  /**
   * Set when the user clicks the **"No, I'm done"** closure chip
   * (`closure-done`). Also cleared when the in-flight stream's terminal
   * `complete` frame lands. Overall feedback may also arm from a
   * `conversation-end` marker in the assistant reply (P3-024) — see
   * {@link parseConversationEndMessage} in {@link _completeStreamingTurn}.
   */
  private _armOverallFeedbackAfterComplete = false;

  /**
   * Whether the end-of-chat overall feedback card is on screen.
   * Cleared when the user sends another message (restart) so it does
   * not stick to later turns; re-armed on the next closure-done /
   * `conversation-end`.
   */
  @state()
  private _overallFeedbackActive = false;

  /**
   * Sticky-once-clicked overall sentiment, mirroring per-turn
   * {@link MwiAssistantTurn.feedbackSubmitted}.
   */
  @state()
  private _overallFeedbackSubmitted: 'positive' | 'negative' | undefined =
    undefined;

  /**
   * Overall negative follow-up card is mounted and the submit event
   * has not yet fired — mirrors per-turn `feedbackPending`.
   */
  @state()
  private _overallFeedbackPending = false;

  /**
   * Cancels {@link MWI_ASSISTANT_REQUEST_OPEN} /
   * {@link MWI_ASSISTANT_REQUEST_MINIMIZE} listeners registered on `window`.
   */
  private _windowRequestAbort: AbortController | null = null;

  /**
   * User-resized frame height in px, or `null` for the Figma default
   * ({@link WIDGET_FRAME_HEIGHT_DEFAULT_PX}). Written by header drag
   * (P4-005) and mirrored to `--mwi-assistant-size-widget-height`.
   */
  private _userFrameHeightPx: number | null = null;

  /**
   * Horizontal reposition offset from the default bottom-right anchor.
   * Positive values move the widget left (increase `right` inset).
   */
  private _userOffsetRightPx = 0;

  /**
   * Vertical reposition offset from the default bottom-right anchor.
   * Positive values move the widget up (increase `bottom` inset).
   */
  private _userOffsetBottomPx = 0;

  /** Active header-drag gesture state, or `null` when idle. */
  private _headerDrag: {
    pointerId: number;
    startX: number;
    startY: number;
    startHeight: number;
    startOffsetRight: number;
    startOffsetBottom: number;
  } | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    console.debug('mwi-assistant >> initialized');
    this._syncLauncherVisibleAttribute();
    this._syncMinHeight();
    this._syncFrameGeometry();
    this._windowRequestAbort?.abort();
    this._windowRequestAbort = new AbortController();
    const signal = this._windowRequestAbort.signal;
    window.addEventListener(
      MWI_ASSISTANT_REQUEST_OPEN,
      this._onWindowRequestOpen,
      { signal }
    );
    window.addEventListener(
      MWI_ASSISTANT_REQUEST_MINIMIZE,
      this._onWindowRequestMinimize,
      { signal }
    );
    this._disclaimerAccepted = this._readDisclaimerAcceptance();
    if (!this.autoOpen) {
      this._visibility = 'minimized';
    }
    /* Mint a conversation when auto-open and the disclaimer was
       accepted in a prior page-load (sessionStorage hit). Host-
       controlled open (`auto-open="false"`) defers until open(). */
    this._tryInitSession();
  }

  /**
   * Tear-down on detach. Cancels in-flight thinking-indicator timers
   * AND aborts any open SSE stream (P1-058) so no callback fires
   * against a disconnected component — visible as a console warning
   * today, harder to diagnose once production telemetry catches it.
   *
   * Intentionally does NOT call `endSession` on disconnect: the host
   * may be re-attaching the widget (e.g. SPA route swap), and we'd
   * rather preserve the conversation if the same element comes back
   * up. The server's 30-min idle eviction reaps anything we leak.
   */
  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._windowRequestAbort?.abort();
    this._windowRequestAbort = null;
    this._headerDrag = null;
    this._clearThinkingTimers();
    this._activeGeneration += 1;
    this._generating = false;
    this._abortInflightStream();
    /* P3-023b-1: drop in-flight negative-reason card state without
       firing synthetic events. The host page detached us; firing
       `mwi-assistant-feedback-submit` against a listener that may
       be gone (or against a host that didn't ask for the event)
       would be noise, not signal. The thumb-down sticky state on
       the row is preserved. */
    this._clearPendingFeedbackState();
    this._resetFeedbackThankYouWorkflow();
  }

  /**
   * Auth-aware `fetch` for any widget-initiated API call (P1-057).
   *
   * Public so the dev-harness probe panel (`index.html`) can route
   * its session / delete-session calls through the same code path
   * the in-widget consumers (P1-058 SSE; P3-022 feedback) will use.
   * The full behaviour — lazy broker fetch, in-memory token cache,
   * pre-emptive refresh, 401-then-refresh-then-retry, pass-through
   * mode when `tokenEndpoint` is empty, concurrency coalescing —
   * lives in `./auth-client.ts`; this method is just the seam that
   * binds the helper to *this* element's `tokenEndpoint` and event
   * dispatch target.
   *
   * Failures bubble out as a {@link AuthError} *and* a
   * `mwi-assistant-auth-error` {@link CustomEvent} on this element
   * (`{ kind, status? }` detail; never the token, never PII — see
   * `planning/AUTHENTICATION.md` §13).
   */
  public authedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const authConfigKey = `${this.apiBase ?? ''}\0${this.tokenEndpoint}`;
    if (
      this._authClient === null ||
      this._authClientConfigKey !== authConfigKey
    ) {
      this._authClient = createAuthClient({
        apiBase: this.apiBase,
        tokenEndpoint: this.tokenEndpoint,
        host: this,
      });
      this._authClientConfigKey = authConfigKey;
    }
    return this._authClient.authedFetch(input, init);
  }

  override firstUpdated(_changed: PropertyValues): void {
    /* Move keyboard focus to the accept button on first paint when the
       modal is showing so screen-reader users land on the actionable
       control rather than wherever the host page focus happened to be. */
    if (!this._disclaimerAccepted) {
      this._focusAcceptButton();
    }
  }

  override updated(changed: PropertyValues): void {
    super.updated(changed);
    if (changed.has('_visibility')) {
      this._reflectVisibilityAttribute();
    }
    if (changed.has('launcherVisible')) {
      this._syncLauncherVisibleAttribute();
    }
    if (changed.has('minHeight')) {
      this._syncMinHeight();
    }
  }

  /**
   * Mirrors the internal `_visibility` state to a `data-visibility`
   * attribute on the host element. Drives the `:host([data-visibility=…])`
   * style rules that shrink the host width when the widget is collapsed
   * down to the launcher button. Also gives host-page CSS a stable hook
   * (`mwi-assistant[data-visibility='minimized']`) for things like the
   * pulsing-while-active treatment that lands in P4-007.
   */
  private _reflectVisibilityAttribute(): void {
    this.setAttribute('data-visibility', this._visibility);
  }

  private _syncLauncherVisibleAttribute(): void {
    this.setAttribute(
      'data-launcher-visible',
      this.launcherVisible ? 'true' : 'false'
    );
  }

  /**
   * Mirrors {@link minHeight} to the `--mwi-assistant-size-widget-min-height`
   * custom property on the host element. Removing the attribute restores the
   * bundled default from {@link tokensStyles}.
   */
  private _syncMinHeight(): void {
    const value = this.minHeight.trim();
    if (value) {
      this.style.setProperty('--mwi-assistant-size-widget-min-height', value);
    } else {
      this.style.removeProperty('--mwi-assistant-size-widget-min-height');
    }
  }

  /**
   * Mirrors user resize / reposition state to host inline styles and the
   * `--mwi-assistant-size-widget-height` custom property consumed by `.frame`.
   */
  private _syncFrameGeometry(): void {
    const height = this._userFrameHeightPx ?? WIDGET_FRAME_HEIGHT_DEFAULT_PX;
    this.style.setProperty('--mwi-assistant-size-widget-height', `${height}px`);

    if (this._userOffsetRightPx !== 0) {
      this.style.setProperty(
        'right',
        `calc(var(--mwi-assistant-space-6) + ${this._userOffsetRightPx}px)`
      );
    } else {
      this.style.removeProperty('right');
    }

    if (this._userOffsetBottomPx !== 0) {
      this.style.setProperty(
        'bottom',
        `calc(var(--mwi-assistant-space-6) + ${this._userOffsetBottomPx}px)`
      );
    } else {
      this.style.removeProperty('bottom');
    }
  }

  private _resolveWidgetMinHeightPx(): number {
    const raw = getComputedStyle(this)
      .getPropertyValue('--mwi-assistant-size-widget-min-height')
      .trim();
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : WIDGET_FRAME_HEIGHT_MIN_PX;
  }

  private _resolveWidgetMaxHeightPx(): number {
    const raw = getComputedStyle(this)
      .getPropertyValue('--mwi-assistant-size-widget-max-height')
      .trim();
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : WIDGET_FRAME_HEIGHT_MAX_PX;
  }

  private _clampFrameHeight(heightPx: number): number {
    const min = this._resolveWidgetMinHeightPx();
    const max = Math.min(this._resolveWidgetMaxHeightPx(), this._viewportFrameCapPx());
    return Math.min(Math.max(heightPx, min), max);
  }

  /** Viewport guard matching the `.frame` max-height calc. */
  private _viewportFrameCapPx(): number {
    const spaceRaw = getComputedStyle(this)
      .getPropertyValue('--mwi-assistant-space-6')
      .trim();
    const space = Number.parseFloat(spaceRaw) || 24;
    return window.innerHeight - 2 * space;
  }

  private _onHeaderBrandPointerDown(event: PointerEvent): void {
    if (event.button !== 0 || this._visibility !== 'expanded') {
      return;
    }
    const frame = this.shadowRoot?.querySelector<HTMLElement>('.frame');
    if (!frame) {
      return;
    }
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    if (typeof target.setPointerCapture === 'function') {
      target.setPointerCapture(event.pointerId);
    }
    const startHeight =
      this._userFrameHeightPx ?? WIDGET_FRAME_HEIGHT_DEFAULT_PX;
    this._headerDrag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startHeight,
      startOffsetRight: this._userOffsetRightPx,
      startOffsetBottom: this._userOffsetBottomPx,
    };
  }

  private _onHeaderBrandPointerMove(event: PointerEvent): void {
    const drag = this._headerDrag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    event.preventDefault();
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    /* Bottom-anchored: drag up (negative deltaY) grows the frame. */
    this._userFrameHeightPx = this._clampFrameHeight(drag.startHeight - deltaY);
    this._userOffsetRightPx = drag.startOffsetRight - deltaX;
    this._syncFrameGeometry();
  }

  private _onHeaderBrandPointerUp(event: PointerEvent): void {
    const drag = this._headerDrag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    const target = event.currentTarget as HTMLElement;
    if (
      typeof target.hasPointerCapture === 'function' &&
      target.hasPointerCapture(event.pointerId)
    ) {
      target.releasePointerCapture(event.pointerId);
    }
    this._headerDrag = null;
  }

  /**
   * Expand the chat frame (same as tapping the launcher). Dispatches
   * {@link MWI_ASSISTANT_OPENED_EVENT} when the state changes.
   */
  open(): void {
    this._onOpen();
  }

  /**
   * Hide the chat frame behind the launcher when expanded, preserving the
   * session (same as the header minimize control). Dispatches
   * {@link MWI_ASSISTANT_MINIMIZED_EVENT} when the state changes.
   */
  minimize(): void {
    this._onMinimize();
  }

  private _requestEventTargetsThisInstance(event: Event): boolean {
    const raw = (
      event as CustomEvent<MwiAssistantRequestEventDetail>
    ).detail?.assistantInstanceId;
    const requested =
      typeof raw === 'string' && raw.length > 0 ? raw : undefined;
    if (requested === undefined) {
      return true;
    }
    return (
      this.assistantInstanceId !== '' &&
      this.assistantInstanceId === requested
    );
  }

  private _onWindowRequestOpen = (event: Event): void => {
    if (!this._requestEventTargetsThisInstance(event)) {
      return;
    }
    this._onOpen();
  };

  private _onWindowRequestMinimize = (event: Event): void => {
    if (!this._requestEventTargetsThisInstance(event)) {
      return;
    }
    this._onMinimize();
  };

  /**
   * Reads the disclaimer acceptance flag from `sessionStorage`. Defensive:
   * `sessionStorage` can throw in private-mode iframes or environments
   * where storage is disabled — the widget treats those as "not accepted"
   * and re-prompts.
   */
  private _readDisclaimerAcceptance(): boolean {
    try {
      return (
        window.sessionStorage.getItem(DISCLAIMER_STORAGE_KEY) === '1'
      );
    } catch {
      return false;
    }
  }

  private _writeDisclaimerAcceptance(): void {
    try {
      window.sessionStorage.setItem(DISCLAIMER_STORAGE_KEY, '1');
    } catch {
      /* Storage unavailable — accept-in-memory only. The user will be
         re-prompted on page reload, which is the correct safe default. */
    }
  }

  private _focusAcceptButton(): void {
    const button = this.shadowRoot?.querySelector<HTMLButtonElement>(
      '.disclaimer__accept'
    );
    button?.focus();
  }

  /**
   * Focus the Cancel button in the close-confirmation modal. Cancel —
   * not the destructive "End conversation" — is the default focus
   * target so an accidental Enter or Space press cannot end the
   * session. This is the whole reason P1-056 exists.
   */
  private _focusCloseConfirmCancel(): void {
    const button = this.shadowRoot?.querySelector<HTMLButtonElement>(
      '[part="close-confirm-cancel"]'
    );
    button?.focus();
  }

  /**
   * Focus the header X button. Called after Cancel/Esc dismisses the
   * confirmation modal so the keyboard user's focus ring lands back
   * on the control they just dismissed — standard modal hygiene.
   */
  private _focusHeaderCloseButton(): void {
    const button = this.shadowRoot?.querySelector<HTMLButtonElement>(
      '[part="close-button"]'
    );
    button?.focus();
  }

  private _onAcceptDisclaimer(): void {
    this._disclaimerAccepted = true;
    this._writeDisclaimerAcceptance();
    /* Mint the conversation when the frame is visible so the round-
       trip latency is hidden behind the welcome bubble. With host-
       controlled open this runs only after open(); errors are
       swallowed inside `_initSession` (logged-only) — the user can
       still type a message; the next submit will retry via the
       `_streamMessage` race-safe path. */
    this._tryInitSession();
    this.dispatchEvent(
      new CustomEvent(DISCLAIMER_ACCEPTED_EVENT, {
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Minimize the widget. Hides the chat frame behind the launcher while
   * **preserving** the in-progress session — the transcript, the input
   * draft, and the conversation id all survive a subsequent re-open.
   * Dispatches {@link MWI_ASSISTANT_MINIMIZED_EVENT} so the host can
   * react (analytics today, nav-bar pulse animation in P4-007).
   *
   * No-op when already minimized or closed; we never want to fire a
   * second event for a state we are already in. The P1-056 close
   * confirmation deliberately does *not* gate this path — minimize is
   * non-destructive (the session is preserved), so adding a confirm
   * step would just be friction.
   *
   * If the close confirmation modal happens to be open when minimize
   * fires (unlikely — the chat-surface is `inert`, but a host page
   * could programmatically click the button), dismiss it first so the
   * widget doesn't re-render with a stale modal floating over the
   * launcher.
   */
  private _onMinimize(): void {
    if (this._visibility !== 'expanded') {
      return;
    }
    this._closeConfirmOpen = false;
    this._visibility = 'minimized';
    this._dispatchVisibilityEvent(MWI_ASSISTANT_MINIMIZED_EVENT);
  }

  /**
   * Header X click handler. Per REQUIREMENTS.md §4.4 "Confirm on close",
   * this does **not** end the session directly — it opens the P1-056
   * confirmation alertdialog ({@link _closeConfirmOpen}) so the user
   * has a chance to back out of an accidental click. The actual session
   * teardown lives in {@link _onConfirmClose}.
   *
   * No-op when the widget is already minimized or closed (the X isn't
   * visible in those states anyway, but the guard mirrors the
   * minimize/open guards for consistency) or when the confirmation
   * modal is already open (re-clicking should not re-fire the focus
   * effect or otherwise churn).
   */
  private _onClose(): void {
    if (this._visibility !== 'expanded') {
      return;
    }
    if (this._closeConfirmOpen) {
      return;
    }
    this._closeConfirmOpen = true;
    /* Focus the safer (non-destructive) Cancel button after the modal
       renders so an accidental Enter press *does not* end the session.
       This is the whole point of P1-056 — the destructive button is
       reachable, but not the default. */
    this.updateComplete.then(() => this._focusCloseConfirmCancel());
  }

  /**
   * Dismiss the close-confirmation modal without ending the session.
   * Wired to the Cancel button, the modal's Escape keydown, and the
   * scrim click. Returns keyboard focus to the header X so the user's
   * focus ring lands back on the control they just dismissed —
   * standard modal hygiene.
   *
   * Fires no event: hosts must never observe a "false" close. The
   * widget remains in the `expanded` state with transcript and draft
   * input untouched.
   */
  private _onCancelClose(): void {
    if (!this._closeConfirmOpen) {
      return;
    }
    this._closeConfirmOpen = false;
    this.updateComplete.then(() => this._focusHeaderCloseButton());
  }

  /**
   * Confirm the close. This is the path that actually **ends the
   * session** — the transcript and any draft input are cleared so
   * reopening starts fresh, matching REQUIREMENTS.md §4.4 "close ends
   * session — conversation history is NOT saved after session end".
   *
   * Disclaimer acceptance is intentionally *not* reset: per
   * REQUIREMENTS.md §4 the legal acceptance is per browser session,
   * not per chat session, and forcing a re-accept on every close would
   * be hostile UX.
   *
   * Dispatches {@link MWI_ASSISTANT_CLOSED_EVENT}. No-op when the
   * confirmation modal is not open (defensive — the button is only
   * rendered while `_closeConfirmOpen` is true, but a stray
   * programmatic invocation should not destroy state).
   */
  private _onConfirmClose(): void {
    if (!this._closeConfirmOpen) {
      return;
    }
    this._performClose();
  }

  /**
   * End the chat session and hide the widget behind the launcher.
   * Shared by the P1-056 confirmation path and the P3-024c
   * thank-you auto-dismiss path (overall feedback submit).
   */
  private _performClose(): void {
    this._closeConfirmOpen = false;
    this._resetFeedbackThankYouWorkflow();
    this._visibility = 'closed';
    this._endSession();
    this._dispatchVisibilityEvent(MWI_ASSISTANT_CLOSED_EVENT);
  }

  /**
   * Cancel the thank-you auto-dismiss timer and hide the overlay.
   * Used on detach, session teardown, re-open from closed, and when
   * {@link _performClose} runs before the timer fires.
   */
  private _resetFeedbackThankYouWorkflow(): void {
    if (this._feedbackThankYouTimer !== null) {
      window.clearTimeout(this._feedbackThankYouTimer);
      this._feedbackThankYouTimer = null;
    }
    this._feedbackThankYouOpen = false;
  }

  /**
   * Clear end-of-chat overall feedback UI state (P3-024a / P3-024c).
   * Uses an explicit `| undefined` union so resets satisfy
   * `exactOptionalPropertyTypes` (optional `?` props cannot be
   * assigned `undefined` directly).
   */
  private _resetOverallFeedbackWorkflow(): void {
    this._armOverallFeedbackAfterComplete = false;
    this._overallFeedbackActive = false;
    this._overallFeedbackSubmitted = undefined;
    this._overallFeedbackPending = false;
  }

  /**
   * Hide the overall-experience card because the user continued the
   * chat after **"No, I'm done"**. Does **not** clear
   * {@link _armOverallFeedbackAfterComplete} — a closure-done click
   * arms that flag first, then appends the user turn, and the
   * upcoming `complete` must still show the card.
   *
   * A thumbs-down reason card still open is flushed as
   * `followUp: 'abandoned'` so the negative is not lost; skipping
   * the prompt with no thumb click records nothing.
   */
  private _hideOverallFeedbackForContinuedConversation(): void {
    if (!this._overallFeedbackActive) {
      return;
    }
    this._flushPendingOverallNegativeAsAbandoned();
    this._overallFeedbackActive = false;
    this._overallFeedbackSubmitted = undefined;
    this._overallFeedbackPending = false;
  }

  /**
   * Show the post–end-of-chat thank-you overlay and schedule widget
   * close. Fires when overall feedback is *dispatched* (positive or
   * negative submit) — not when the HTTP round-trip resolves
   * (fire-and-forget policy).
   */
  private _showFeedbackThankYouAndScheduleClose(): void {
    if (this._visibility !== 'expanded') {
      return;
    }
    this._resetFeedbackThankYouWorkflow();
    this._feedbackThankYouOpen = true;
    this._feedbackThankYouTimer = window.setTimeout(() => {
      this._feedbackThankYouTimer = null;
      if (!this._feedbackThankYouOpen) {
        return;
      }
      this._feedbackThankYouOpen = false;
      if (this._visibility === 'closed') {
        return;
      }
      this._performClose();
    }, FEEDBACK_THANK_YOU_AUTO_DISMISS_MS);
  }

  /**
   * Keydown handler attached to the close-confirmation modal so the
   * Escape key behaves like Cancel — standard alertdialog UX. We
   * stop propagation so the host page's own Esc handlers (e.g. a
   * Sitecore lightbox) don't also fire from the same press.
   */
  private _onCloseConfirmKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this._onCancelClose();
    }
  }

  /**
   * Open (or re-open) the widget from the launcher. Brings the chat
   * frame back to the foreground and dispatches
   * {@link MWI_ASSISTANT_OPENED_EVENT}. State preservation depends on
   * the previous visibility:
   *
   *   - re-open from `minimized` → transcript + draft preserved
   *   - re-open from `closed`    → fresh session (transcript already
   *                                 cleared by {@link _onClose})
   *
   * After the next render lands we move keyboard focus into the input
   * (or the disclaimer Accept button if the legal gate is still up —
   * needed when the first paint was launcher-only via `auto-open="false"`).
   */
  private _onOpen(): void {
    if (this._visibility === 'expanded') {
      return;
    }
    const reopeningFromClosed = this._visibility === 'closed';
    this._visibility = 'expanded';
    if (reopeningFromClosed) {
      /* Belt-and-suspenders: a stale thank-you timer must not fire
         after the user starts a fresh chat and wipe the new session;
         overall feedback sticky state must not leak across sessions. */
      this._resetFeedbackThankYouWorkflow();
      this._resetOverallFeedbackWorkflow();
    }
    this._dispatchVisibilityEvent(MWI_ASSISTANT_OPENED_EVENT);
    /* Host-controlled open defers session + history until the frame
       expands; re-open from `closed` also re-runs bootstrap because
       `_endSession` cleared the conversation id. */
    this._tryInitSession();
    this.updateComplete.then(() => {
      if (this._disclaimerAccepted) {
        this._focusInput();
      } else {
        this._focusAcceptButton();
      }
    });
  }

  /**
   * Reset all per-conversation client state. Called on confirmed
   * close (and only close — minimize must preserve everything). The
   * disclaimer acceptance flag is intentionally untouched here; it
   * is keyed to the browser tab/session, not to the chat session.
   *
   * Order matters:
   *   0. **Before any teardown**, auto-flush every per-turn
   *      negative still in `feedbackPending` state as
   *      `followUp: 'abandoned'` (P3-023b-1). Each event must
   *      fire while the conversation and turn ids still exist —
   *      doing this after the wipe would leak `turnId`s whose
   *      owning turns are gone.
   *   1. Tear down any thinking-indicator timers (P1-054).
   *   2. Abort the in-flight SSE stream (P1-058) so no callback
   *      fires after we wipe the conversation id.
   *   3. Fire-and-forget `POST {end-endpoint}` with `{ id }`
   *      the server-side {@link AssistantConversation} is reaped
   *      promptly rather than waiting for the 30-min idle eviction.
   *      Errors are logged-only — the user already chose to leave;
   *      we don't surface a "couldn't end session" message that
   *      would reopen UI they were trying to dismiss.
   *   4. Wipe transcript / draft / conversation id.
   */
  private _endSession(): void {
    this._flushPendingNegativesAsAbandoned();
    this._flushPendingOverallNegativeAsAbandoned();
    this._clearThinkingTimers();
    this._activeGeneration += 1;
    this._generating = false;
    this._abortInflightStream();
    const conversationId = this._conversationId;
    if (conversationId !== null) {
      try {
        const apiClient = this._getApiClient();
        void apiClient.endSession(conversationId).catch((err) => {
          /* eslint-disable-next-line no-console */
          console.warn('[mwi-assistant] endSession failed', err);
        });
      } catch (err) {
        /* eslint-disable-next-line no-console */
        console.warn('[mwi-assistant] endSession could not run', err);
      }
    }
    this._turns = [];
    this._inputValue = '';
    this._conversationId = null;
    this._sessionReady = null;
    this._sessionBootstrapping = false;
    this._showWelcomeTurn = false;
    this._armOverallFeedbackAfterComplete = false;
    this._resetOverallFeedbackWorkflow();
    this._resetFeedbackThankYouWorkflow();
  }

  private _dispatchVisibilityEvent(eventName: string): void {
    this.dispatchEvent(
      new CustomEvent<MwiAssistantVisibilityEventDetail>(eventName, {
        bubbles: true,
        composed: true,
        detail: { conversationId: this._conversationId },
      })
    );
  }

  /**
   * Stable per-turn id. Prefers `crypto.randomUUID` (available in every
   * evergreen browser the widget supports) and falls back to a per-element
   * counter so unit tests in environments without a Web Crypto polyfill
   * still get unique ids.
   */
  private _generateTurnId(): string {
    const cryptoApi = (globalThis as { crypto?: Crypto }).crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
      return cryptoApi.randomUUID();
    }
    this._turnIdCounter += 1;
    return `mwi-turn-${this._turnIdCounter}`;
  }

  private _onInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement | null;
    this._inputValue = target?.value ?? '';
    if (target) {
      this._autoResizeInput(target);
    }
  }

  /**
   * Keydown handler on the textarea. Implements the chat-app convention
   * (Slack, ChatGPT, Discord, Teams) for multiline message inputs:
   *
   *  - **Enter** alone submits the form (matches the previous single-line
   *    `<input>` behaviour and avoids surprising users who never use
   *    multiline messages).
   *  - **Shift+Enter** falls through to the textarea's native handling,
   *    which inserts a newline. Combined with the auto-grow + 4-line
   *    cap (REQUIREMENTS.md §5.1) this gives users a way to compose
   *    multi-line messages without an extra "send" gesture.
   *  - **IME composition** (`event.isComposing`) is respected so an
   *    Enter that commits a CJK candidate doesn't accidentally submit
   *    the message.
   *
   * The Enter→submit path goes through `requestSubmit()` rather than
   * dispatching a synthetic event so the browser's native form-submission
   * pipeline (validation, the standard `submit` event) is used; the
   * existing `_onSubmit` handler picks it up unchanged.
   *
   * Open question for P4-001 review: Liana may prefer Enter→newline +
   * Cmd/Ctrl+Enter→submit (closer to mail composers) once the multiline
   * use case is exercised in UAT. The convention chosen here is the
   * majority-vote default for chat surfaces; revisit at P4-001.
   */
  private _onInputKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      return;
    }
    if (event.shiftKey || event.isComposing) {
      return;
    }
    event.preventDefault();
    /* Send stays disabled while a reply is in flight. Enter must
       not sneak past that — requestSubmit() still fires when the
       submit button is disabled. */
    if (this._generating) {
      return;
    }
    const target = event.currentTarget as HTMLTextAreaElement | null;
    target?.form?.requestSubmit();
  }

  /**
   * Resize the textarea to fit its current content, capped at
   * `--mwi-assistant-size-input-max-height` (default 112px / 4 lines per
   * REQUIREMENTS.md §5.1). Past the cap the textarea's own `overflow-y:
   * auto` takes over and the field scrolls internally instead of growing.
   *
   * Most modern browsers will already do this for us via the
   * `field-sizing: content` CSS we set on `.input` — this method is the
   * fallback for browsers without that property *and* the path our unit
   * tests exercise (happy-dom doesn't compute layout, so we drive the
   * height manually so test expectations are stable).
   *
   * Strategy: clear the inline height, read `scrollHeight` (which is now
   * the content's natural height), then write that back as the inline
   * height. The CSS `max-height` cap handles the upper bound; resetting
   * to baseline first is what lets the field also *shrink* when the
   * user deletes lines.
   */
  private _autoResizeInput(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  /**
   * Submit handler for the input form. Trims the current value, rejects
   * whitespace-only sends, appends a user turn, clears + re-focuses the
   * input, and starts the thinking indicator (P1-054). Wired as the
   * form's `submit` so Enter-to-send works without an extra keydown
   * handler. After clearing, the textarea is also resized back down to
   * its baseline single-line height — otherwise the inline `style.height`
   * we set in {@link _autoResizeInput} would persist and leave a tall
   * empty input.
   *
   * The thinking indicator owns the assistant-side surface from this
   * point until either (a) the first SSE `delta` lands and
   * {@link _beginStreamingAssistantTurn} replaces it with a growing
   * bubble, or (b) the user ends the session, which clears all
   * in-flight timers via {@link _endSession}.
   */
  private _onSubmit(event: Event): void {
    event.preventDefault();
    if (!this._disclaimerAccepted) {
      return;
    }

    const trimmed = this._inputValue.trim();
    if (trimmed.length === 0) {
      return;
    }
    if (!this._tryStartRequest()) {
      return;
    }

    this._appendUserTurn(trimmed);
    this._inputValue = '';
    this._startThinking();
    void this._streamMessage(trimmed);

    this.updateComplete.then(() => {
      const textarea =
        this.shadowRoot?.querySelector<HTMLTextAreaElement>('.input');
      if (textarea) {
        this._autoResizeInput(textarea);
      }
      this._scrollTranscriptToBottom();
      this._focusInput();
    });
  }

  private _appendUserTurn(text: string): void {
    this._hideOverallFeedbackForContinuedConversation();
    this._turns = [
      ...this._turns,
      {
        id: this._generateTurnId(),
        role: 'user',
        text,
        timestamp: new Date(),
      },
    ];
  }

  /**
   * Whether *any* user turn has already landed in the transcript. Drives
   * two pieces of UI today:
   *
   *  - the starter-chip disabled state ({@link _chipsDisabled}) — once a
   *    conversation has begun, the welcome chips are no longer relevant
   *    and must not be re-clickable;
   *  - the input placeholder swap ({@link _inputPlaceholder}) from
   *    "Type your question here…" to "Add your reply here…".
   *
   * Intentionally checks for `role === 'user'` rather than `length > 0`:
   * the welcome turn isn't in `_turns` (it's static markup), but the
   * P1-054 thinking indicator (and the eventual fake-agent placeholder
   * reply) IS, so a `length` check would also flip on whenever an
   * assistant-driven turn lands first. Today only user turns ever lead,
   * but P1-058 may surface a server-side greeting before any user
   * message — keying off the user role keeps both states stable when
   * that lands.
   */
  private get _hasUserTurn(): boolean {
    return this._turns.some((turn) => turn.role === 'user');
  }

  /**
   * Computed disabled state for the welcome-bubble starter chips (P1-052).
   *
   * Disabled when:
   *
   *  - the legal disclaimer modal (P1-051) has not been accepted —
   *    defence-in-depth alongside the `inert` attribute on the chat
   *    surface; a host page that styled the modal differently could
   *    otherwise route a programmatic chip click through; or
   *  - any user turn has already landed — once a conversation has
   *    started the starter prompts are no longer relevant. The rail
   *    disables permanently for the lifetime of the session and only
   *    re-enables after a confirmed close clears the transcript via
   *    {@link _endSession}.
   */
  private get _chipsDisabled(): boolean {
    if (!this._disclaimerAccepted) {
      return true;
    }
    if (!this._showWelcomeTurn) {
      return true;
    }
    return this._hasUserTurn;
  }

  /**
   * Handles a click on one of the welcome-bubble starter chips (P1-052).
   * Per REQUIREMENTS.md §4.2 a chip click is a *one-tap user message*:
   * it goes through the same render path as a typed message — append
   * the user turn, hand off to the P1-054 thinking indicator via
   * {@link _startThinking} so the dots → text → real-reply seam stays
   * the single source of truth for assistant-side rendering.
   *
   * The chip is also disabled (via {@link _chipsDisabled}) once the
   * first user turn lands; the guards below are belt-and-braces for
   * the case where a host page bypasses the disabled state or
   * dispatches a programmatic click while the disclaimer is up.
   *
   * After the turn lands the transcript scrolls to bottom and focus
   * moves into the input — matching the post-send behaviour in
   * {@link _onSubmit} so the user can immediately keep typing.
   */
  private _onStarterPromptClick(chip: StarterPromptChip): void {
    if (!this._disclaimerAccepted) {
      return;
    }
    if (this._hasUserTurn) {
      return;
    }
    if (!this._tryStartRequest()) {
      return;
    }
    const bubbleText = chip.hidden ? chip.label : chip.prompt;
    this._appendUserTurn(bubbleText);
    this._startThinking();
    void this._streamMessage(chip.prompt);

    this.updateComplete.then(() => {
      this._scrollTranscriptToBottom();
      this._focusInput();
    });
  }

  /**
   * Handles a click on a `follow-up-prompt` action chip rendered
   * beneath an assistant bubble (P1-058c). Behavioural contract:
   *
   *  1. Same submission pathway as a typed message and as a
   *     welcome-bubble starter chip (P1-052) — append the user
   *     turn, hand off to the P1-054 thinking indicator, then
   *     drive `_streamMessage` to consume the SSE stream. The
   *     transcript renders the chip's `label` as the user message
   *     verbatim, except the **"No, I'm done"** chip (`closure-done`)
   *     which still shows that label in the bubble but posts
   *     {@link CLOSURE_DONE_BACKEND_MESSAGE} so Azure OpenAI's
   *     self-harm filter does not treat the UI copy as a crisis
   *     phrase.
   *  2. Server-side hints (`action.parameters`) ride along as the
   *     `clientContext` map on the next `/message/stream` request
   *     — same field the agent backlog is expected to surface in
   *     intent-driven prompts (e.g. `{ followUpId: 'returns-eligibility' }`).
   *  3. **One-shot per bubble.** Marks the originating turn's
   *     `actionsConsumed` flag so every chip in the same bubble
   *     disables on the first click. Other turns' chips are
   *     unaffected — follow-up-prompt suggestions are per-turn
   *     alternatives, not a session-wide rail. Decision logged
   *     in `planning/figma/TOKENS.md` *Action chips below
   *     assistant bubble* and ratified at P4-001.
   *
   * Defence-in-depth guards (mirroring {@link _onStarterPromptClick}):
   *  - disclaimer-not-accepted → no-op (the chat surface is
   *    `inert` while the disclaimer is up, but a programmatic
   *    click could still race);
   *  - same-bubble already consumed → no-op (the `disabled`
   *    attribute already prevents the click in HTML, but a host
   *    page that styles around it must not be able to fire
   *    multiple chip-driven turns from one bubble).
   *
   * After the turn lands the transcript scrolls to bottom and
   * keyboard focus moves into the input — matching the
   * post-send behaviour in {@link _onSubmit} and
   * {@link _onStarterPromptClick} so the user can immediately
   * keep typing.
   */
  private _onFollowUpPromptClick(
    action: FollowUpPromptAction,
    originTurnId: string
  ): void {
    if (!this._disclaimerAccepted) {
      return;
    }
    const originTurn = this._turns.find((t) => t.id === originTurnId);
    if (originTurn === undefined || originTurn.actionsConsumed === true) {
      return;
    }
    if (!this._tryStartRequest()) {
      return;
    }

    /* Mark the bubble's chip rail as consumed before the streaming
       handoff so the disabled state is visible on the very next
       render — even if a fast back-to-back click somehow squeezed
       through, the second click would short-circuit on the guard
       above. */
    this._turns = this._turns.map((t) =>
      t.id === originTurnId ? { ...t, actionsConsumed: true } : t
    );

    const isClosureDone =
      action.parameters?.followUpId === CLOSURE_DONE_FOLLOW_UP_ID;
    if (isClosureDone) {
      this._armOverallFeedbackAfterComplete = true;
    }

    const backendMessage = isClosureDone
      ? CLOSURE_DONE_BACKEND_MESSAGE
      : action.label;
    this._appendUserTurn(action.label);
    this._startThinking();
    void this._streamMessage(backendMessage, action.parameters);

    this.updateComplete.then(() => {
      this._scrollTranscriptToBottom();
      this._focusInput();
    });
  }

  /**
   * Handle a click on either thumb in a per-turn feedback row
   * (P3-023a + P3-023b-1). Sticky-once-clicked: the first click on
   * a given turn flips its `feedbackSubmitted` flag, the prompt
   * swaps to "Thanks for the feedback!", and both icons go inert.
   *
   * Dispatch timing diverges by `sentiment` (P3-023b-1):
   *
   *  - **Positive** → fires
   *    {@link MWI_ASSISTANT_FEEDBACK_SUBMIT_EVENT} immediately,
   *    same as P3-023a shipped. The contract has no positive
   *    follow-up flow today (API-CONTRACT.md §5.5).
   *  - **Negative** → flips `feedbackPending = true`, mounts
   *    `<mwi-feedback-reason-card>` (P3-025a) inline beneath the
   *    feedback row (see {@link renderTurn}), and **defers** the
   *    event dispatch until the card resolves through one of:
   *      * Submit (>=1 chip) → {@link _onFeedbackReasons}
   *      * Dismiss / Esc     → {@link _onFeedbackDismiss}
   *      * confirmed close   → auto-flushed by {@link _endSession}
   *                            with `followUp: 'abandoned'`
   *
   * Defence-in-depth guard: if the turn is already submitted (the
   * disabled buttons should prevent this anyway, but a programmatic
   * click could bypass) the handler is a no-op so we never
   * re-mount the card or double-fire the event.
   *
   * Mutates the array via `map` so Lit picks up the change via
   * reference inequality on the affected turn — same idiom used by
   * the P1-058c `actionsConsumed` flip.
   */
  private _onFeedbackClick(
    turn: MwiAssistantTurn,
    sentiment: 'positive' | 'negative'
  ): void {
    if (turn.feedbackSubmitted !== undefined) {
      return;
    }
    const turnId = turn.id;
    if (sentiment === 'negative') {
      this._turns = this._turns.map((t) =>
        t.id === turnId
          ? { ...t, feedbackSubmitted: 'negative', feedbackPending: true }
          : t
      );
      /* Defer dispatch until the negative-reason card resolves. */
      return;
    }
    this._turns = this._turns.map((t) =>
      t.id === turnId ? { ...t, feedbackSubmitted: 'positive' } : t
    );
    this._dispatchFeedbackSubmit({
      turnId,
      sentiment: 'positive',
      kind: 'per-turn',
    });
  }

  /**
   * Handle a click on either thumb in the **overall-experience** card
   * (P3-024a). This is NOT {@link _onFeedbackClick} — that handler is
   * only for the per-turn `Helpful response?` row beneath individual
   * assistant replies (P3-023a). Overall feedback fires after a closure
   * thank-you reply — either because the user tapped **"No, I'm done"**
   * (`closure-done`) or because the assistant message carried the
   * `conversation-end` marker (P3-024 / API-CONTRACT.md §5.3.1). Posts
   * `type: end-of-chat` (no `turnId`) via `kind: 'overall'`.
   */
  private _onOverallFeedbackClick(sentiment: 'positive' | 'negative'): void {
    if (this._overallFeedbackSubmitted !== undefined) {
      return;
    }
    if (sentiment === 'negative') {
      this._overallFeedbackSubmitted = 'negative';
      this._overallFeedbackPending = true;
      return;
    }
    this._overallFeedbackSubmitted = 'positive';
    this._submitOverallPositiveFeedbackAndClose();
  }

  /**
   * Fire end-of-chat positive feedback (fire-and-forget), then show
   * the thank-you overlay and auto-close after
   * {@link FEEDBACK_THANK_YOU_AUTO_DISMISS_MS}. Close does not wait
   * on — or depend on — feedback HTTP success.
   */
  private _submitOverallPositiveFeedbackAndClose(): void {
    void this._dispatchFeedbackSubmit({
      sentiment: 'positive',
      kind: 'overall',
    });
    this._showFeedbackThankYouAndScheduleClose();
  }

  /**
   * Reason-card Submit handler (P3-023b-1). Wired in the template
   * directly on the `<mwi-feedback-reason-card>` element so each
   * card instance reaches its owning per-turn handler without a
   * shared host-level dispatch table — keeps multi-turn pending
   * state independent.
   *
   * Resolves the negative follow-up: clears `feedbackPending` (the
   * card unmounts on the next render), then dispatches the
   * `mwi-assistant-feedback-submit` event with `followUp: 'reasons'`
   * and the user's selected chips. The chip values come from the
   * card's render-order filter so the wire shape is stable.
   *
   * The thumb-down sticky state on the row above is preserved —
   * the user is on record as having said "not helpful" AND
   * elaborated; that is the strongest signal we capture.
   *
   * Defensive guard: if the originating turn is no longer in
   * `feedbackPending` state (e.g. a confirmed close auto-flushed
   * the abandoned variant first, or a stray re-dispatch from a
   * misbehaving host), short-circuit so we never double-fire.
   */
  private _onFeedbackReasons = (
    event: CustomEvent<MwiAssistantFeedbackReasonsEventDetail>
  ): void => {
    const turnId = event.detail.turnId;
    if (turnId === OVERALL_FEEDBACK_CORRELATION_ID) {
      if (this._overallFeedbackPending !== true) {
        return;
      }
      this._overallFeedbackPending = false;
      const reasons = event.detail.reasons as readonly FeedbackReason[];
      this._dispatchFeedbackSubmit({
        sentiment: 'negative',
        kind: 'overall',
        followUp: 'reasons',
        reasons,
      });
      this._showFeedbackThankYouAndScheduleClose();
      return;
    }
    if (turnId === null) {
      return;
    }
    const turn = this._turns.find((t) => t.id === turnId);
    if (turn === undefined || turn.feedbackPending !== true) {
      return;
    }
    this._turns = this._turns.map((t) =>
      t.id === turnId ? { ...t, feedbackPending: false } : t
    );
    /* The reason-card primitive constrains its emitted strings to
       the canonical render-order set, which is exactly the
       FeedbackReason union. The cast avoids re-validating values
       the producer already guarantees. */
    const reasons = event.detail.reasons as readonly FeedbackReason[];
    this._dispatchFeedbackSubmit({
      turnId,
      sentiment: 'negative',
      kind: 'per-turn',
      followUp: 'reasons',
      reasons,
    });
  };

  /**
   * Reason-card Dismiss handler (P3-023b-1). Wired identically to
   * {@link _onFeedbackReasons}. Fires on either the Dismiss button
   * click or the Esc key (the card primitive normalises both into
   * the same event — see `feedback-reason-card.ts`).
   *
   * Resolves the negative follow-up: clears `feedbackPending` (the
   * card unmounts on the next render), then dispatches the
   * `mwi-assistant-feedback-submit` event with `followUp: 'dismissed'`
   * and **no** `reasons` field. The user is on record as having said
   * "not helpful" but declined to elaborate — the contract handles
   * this as a distinct terminal state from `'reasons'` so analytics
   * can split the two (API-CONTRACT.md §5.5).
   *
   * **Overall (end-of-chat) exception:** dismiss resets the overall
   * thumbs row to its initial state and fires **no** submit event or
   * API call — see the `OVERALL_FEEDBACK_CORRELATION_ID` branch.
   *
   * Same defensive `feedbackPending` guard as the Submit handler.
   */
  private _onFeedbackDismiss = (
    event: CustomEvent<MwiAssistantFeedbackDismissEventDetail>
  ): void => {
    const turnId = event.detail.turnId;
    if (turnId === OVERALL_FEEDBACK_CORRELATION_ID) {
      if (this._overallFeedbackPending !== true) {
        return;
      }
      /* End-of-chat dismiss is a bail-out, not a terminal negative
         record — the user may have mis-tapped thumbs-down or changed
         their mind. Reset to the initial prompt so they can pick
         again (thumbs-up still auto-closes per P3-024b). */
      this._overallFeedbackPending = false;
      this._overallFeedbackSubmitted = undefined;
      return;
    }
    if (turnId === null) {
      return;
    }
    const turn = this._turns.find((t) => t.id === turnId);
    if (turn === undefined || turn.feedbackPending !== true) {
      return;
    }
    this._turns = this._turns.map((t) =>
      t.id === turnId ? { ...t, feedbackPending: false } : t
    );
    this._dispatchFeedbackSubmit({
      turnId,
      sentiment: 'negative',
      kind: 'per-turn',
      followUp: 'dismissed',
    });
  };

  /**
   * Single dispatch seam for {@link MWI_ASSISTANT_FEEDBACK_SUBMIT_EVENT}
   * AND the matching `POST /api/feedback` integration call (P3-023b-2).
   * Centralised so all four call sites (positive, reasons, dismissed,
   * abandoned) share the same `bubbles` / `composed` defaults, the
   * same event-name source of truth, and the same wire mapping.
   *
   * **Order of operations:**
   *   1. Dispatch the host-page CustomEvent first. It is the
   *      pre-existing surface (P3-023a + P3-023b-1) and host
   *      analytics consumers depend on it firing synchronously
   *      relative to the user's click.
   *   2. Translate `detail` 1:1 onto the `FeedbackPayload` wire
   *      shape (see `API-CONTRACT.md` §5.5 + §5.5.4 + §5.5.5) and
   *      fire-and-forget `apiClient.submitFeedback`. The optional
   *      `followUp` / `reasons` fields are spread conditionally so
   *      the JSON body omits them entirely on the positive case
   *      rather than serialising `"followUp": undefined` (which
   *      would round-trip but read dirty on the wire).
   *
   * **Error policy: silent log-and-drop.** Any
   * {@link FeedbackError} (or generic exception) is surfaced via
   * `console.warn` and dropped. Rationale: feedback is an
   * analytics event, not a user-action that needs confirmation.
   * The user has already moved on (the thumb-down sticky state
   * stays pressed; the prompt stays "Thanks for the feedback!").
   * A failed submit is a server-side observability problem, not a
   * user-facing one — so we deliberately do NOT revert the sticky
   * state, surface an inline error, or render a retry button.
   * `'unauthorized'` specifically only reaches us after the P1-057
   * auth-retry layer has already failed once, so additional widget
   * handling there is wasted.
   *
   * **Close-race policy: fire-and-forget.** The abandoned-flush
   * fires `submitFeedback` calls that race against the same
   * `_endSession`'s end-session POST. We
   * deliberately do NOT pass an `AbortSignal` — the abandoned
   * signal must reach the server even if the user navigates away
   * mid-flush, and aborting on close would defeat the analytics
   * purpose of the event. Server-side dedupe / ordering isn't our
   * problem (the abandoned record means "user closed without
   * elaborating", which doesn't depend on session state).
   *
   * **Defence-in-depth: missing `_conversationId`.** Should never
   * fire in production — every dispatch path requires either an
   * eagerly-minted session (positive / negative-resolution) or an
   * id that's still present at the start of `_endSession`
   * (abandoned). The guard exists so a future regression that
   * breaks one of those invariants surfaces as a logged warning
   * rather than an `undefined` smuggled onto the wire.
   */
  private _dispatchFeedbackSubmit(
    detail: MwiAssistantFeedbackEventDetail
  ): Promise<void> {
    this.dispatchEvent(
      new CustomEvent<MwiAssistantFeedbackEventDetail>(
        MWI_ASSISTANT_FEEDBACK_SUBMIT_EVENT,
        {
          bubbles: true,
          composed: true,
          detail,
        }
      )
    );

    const conversationId = this._conversationId;
    if (conversationId === null) {
      /* eslint-disable-next-line no-console */
      console.warn(
        '[mwi-assistant] feedback submit skipped — no conversation id'
      );
      return Promise.resolve();
    }

    const payload: FeedbackPayload =
      detail.kind === 'overall'
        ? {
            type: 'end-of-chat',
            conversationId,
            sentiment: detail.sentiment,
            ...(detail.followUp !== undefined && {
              followUp: detail.followUp,
            }),
            ...(detail.reasons !== undefined && { reasons: detail.reasons }),
          }
        : {
            type: 'per-turn',
            conversationId,
            turnId: detail.turnId!,
            sentiment: detail.sentiment,
            ...(detail.followUp !== undefined && {
              followUp: detail.followUp,
            }),
            ...(detail.reasons !== undefined && { reasons: detail.reasons }),
          };

    /* Returned promise so tests (or future call sites) can optionally
       await the HTTP round-trip. Production call sites remain
       fire-and-forget via `void`. Rejections are swallowed here so
       a failed submit never leaks an unhandled rejection. */
    return (async (): Promise<void> => {
      try {
        const apiClient = this._getApiClient();
        await apiClient.submitFeedback(payload);
      } catch (err) {
        /* eslint-disable-next-line no-console */
        console.warn('[mwi-assistant] feedback submit failed', err);
      }
    })();
  }

  /**
   * Auto-flush every per-turn negative still in
   * `feedbackPending` state as `followUp: 'abandoned'`
   * (P3-023b-1). Called from {@link _endSession} BEFORE the
   * transcript wipe so each event fires while the conversation
   * and turn ids still exist — order matters; firing after the
   * wipe would leak a `turnId` whose owning turn is gone.
   *
   * The pending flag is intentionally not cleared per-turn — the
   * caller (`_endSession`) wipes `_turns = []` immediately
   * afterwards, so an additional `map` would be wasted work.
   *
   * No-op when nothing is pending — the common case for a chat
   * that ended after exclusively positive feedback (or no
   * feedback at all).
   */
  private _flushPendingNegativesAsAbandoned(): void {
    for (const turn of this._turns) {
      if (turn.feedbackPending === true) {
        this._dispatchFeedbackSubmit({
          turnId: turn.id,
          sentiment: 'negative',
          kind: 'per-turn',
          followUp: 'abandoned',
        });
      }
    }
  }

  /**
   * Auto-flush an overall negative still in `_overallFeedbackPending`
   * as `followUp: 'abandoned'` (P3-024a). Called from
   * {@link _endSession} before session teardown.
   */
  private _flushPendingOverallNegativeAsAbandoned(): void {
    if (this._overallFeedbackPending !== true) {
      return;
    }
    this._overallFeedbackPending = false;
    this._dispatchFeedbackSubmit({
      sentiment: 'negative',
      kind: 'overall',
      followUp: 'abandoned',
    });
  }

  /**
   * Drop any in-flight `feedbackPending` flags WITHOUT dispatching
   * synthetic events (P3-023b-1). Invoked from
   * {@link disconnectedCallback} — the host page detached us, so
   * firing events would be noise for a listener that may already
   * be torn down. The thumb-down sticky state
   * (`feedbackSubmitted: 'negative'`) is preserved; only the
   * "card is mounted" flag is reset so a hypothetical re-attach
   * doesn't resurrect a stale card.
   */
  private _clearPendingFeedbackState(): void {
    const hasPerTurnPending = this._turns.some(
      (t) => t.feedbackPending === true
    );
    if (hasPerTurnPending) {
      this._turns = this._turns.map((t) =>
        t.feedbackPending === true ? { ...t, feedbackPending: false } : t
      );
    }
    if (this._overallFeedbackPending) {
      this._overallFeedbackPending = false;
    }
  }

  /**
   * Append a transient thinking indicator (P1-054) to the transcript
   * and arm the dots → text phase swap. Today the indicator vanishes
   * the moment the first SSE `delta` frame replaces it with a
   * streaming bubble (see {@link _beginStreamingAssistantTurn}).
   *
   * Behaviour summary, per REQUIREMENTS.md §5.2:
   *  - Phase 1 (`'dots'`): rendered immediately on submit, three pulsing
   *    dots, lasts {@link THINKING_TEXT_DELAY_MS}ms.
   *  - Phase 2 (`'text'`): swaps the same turn to a random line from
   *    {@link THINKING_TEXT_MESSAGES} at the 3 s mark.
   *
   * Cancellation:
   *  - Stop generating, confirmed close, and detach drop the timer
   *    and abort the request. A second submit while a request is in
   *    flight is ignored — it does not replace this indicator.
   *  - {@link _endSession} clears the timer + aborts the stream so a
   *    close-mid-thinking does not silently re-emit a bubble.
   *  - {@link disconnectedCallback} clears the timer + aborts the
   *    stream so a host page detaching the widget mid-thinking does
   *    not leak a fired-after-detach timer or callback.
   */
  private _startThinking(): void {
    this._clearThinkingTimers();
    if (this._thinkingTurnId !== null) {
      this._removeThinkingTurn();
    }

    const id = this._generateTurnId();
    this._thinkingTurnId = id;
    this._turns = [
      ...this._turns,
      {
        id,
        role: 'assistant',
        text: '',
        timestamp: new Date(),
        thinking: 'dots',
        thinkingText: pickThinkingText(),
      },
    ];

    /* Phase swap: dots → text. Fires exactly once at the 3 s mark per
       REQUIREMENTS.md §5.2. We mutate by mapping over `_turns` so Lit
       picks up the change via reference inequality (the turn object is
       replaced, not patched in place). */
    this._thinkingPhaseTimer = window.setTimeout(() => {
      this._thinkingPhaseTimer = null;
      if (this._thinkingTurnId !== id) {
        return;
      }
      this._turns = this._turns.map((turn) =>
        turn.id === id ? { ...turn, thinking: 'text' as const } : turn
      );
    }, THINKING_TEXT_DELAY_MS);
  }

  /**
   * Cancel the thinking-indicator phase-swap timer. Idempotent —
   * safe to call from multiple teardown paths
   * (`disconnectedCallback`, `_endSession`, every fresh
   * `_startThinking`, every SSE terminal frame).
   *
   * Does **not** remove the in-flight thinking turn from `_turns`;
   * the caller decides whether the turn should be replaced (its
   * slot is about to be filled by the streaming bubble) or removed
   * outright (session was ended).
   */
  private _clearThinkingTimers(): void {
    if (this._thinkingPhaseTimer !== null) {
      window.clearTimeout(this._thinkingPhaseTimer);
      this._thinkingPhaseTimer = null;
    }
  }

  /**
   * Drop the in-flight thinking turn from the transcript. Used when
   * a fresh submit pre-empts the previous turn's indicator before
   * the SSE stream produced any deltas.
   */
  private _removeThinkingTurn(): void {
    if (this._thinkingTurnId === null) {
      return;
    }
    const id = this._thinkingTurnId;
    this._turns = this._turns.filter((turn) => turn.id !== id);
    this._thinkingTurnId = null;
  }

  /* =============================================================== *
   *  P1-058: session lifecycle + SSE message streaming               *
   * =============================================================== */

  /**
   * Lazily build the api-client and rebuild it when `apiBase` shifts
   * underneath us. Mirrors the lazy-construction pattern used by
   * {@link authedFetch} for the auth-client.
   */
  private _getApiClient(): AssistantApiClient {
    const base = this.apiBase ?? '';
    const sessionEndpoint = this.sessionEndpoint ?? DEFAULT_SESSION_ENDPOINT;
    const messageEndpoint = this.messageEndpoint ?? DEFAULT_MESSAGE_ENDPOINT;
    const messageStreamEndpoint =
      this.messageStreamEndpoint ?? DEFAULT_MESSAGE_STREAM_ENDPOINT;
    const historyEndpoint = this.historyEndpoint ?? DEFAULT_HISTORY_ENDPOINT;
    const endEndpoint = this.endEndpoint ?? DEFAULT_END_ENDPOINT;
    const feedbackEndpoint =
      this.feedbackEndpoint ?? DEFAULT_FEEDBACK_ENDPOINT;
    const configKey = JSON.stringify({
      base,
      sessionEndpoint,
      messageEndpoint,
      messageStreamEndpoint,
      historyEndpoint,
      endEndpoint,
      feedbackEndpoint,
    });
    if (
      this._apiClient === null ||
      this._apiClientConfigKey !== configKey
    ) {
      this._apiClient = createAssistantApiClient({
        apiBase: base,
        sessionEndpoint,
        messageEndpoint,
        messageStreamEndpoint,
        historyEndpoint,
        endEndpoint,
        feedbackEndpoint,
        fetch: (input, init) => this.authedFetch(input, init),
      });
      this._apiClientConfigKey = configKey;
    }
    return this._apiClient;
  }

  /**
   * Start session bootstrap when prerequisites are met: disclaimer
   * accepted, and either `auto-open` (eager on mount / accept) or the
   * chat frame is expanded (host-controlled {@link open}). No-op when
   * a session is already minting or ready.
   */
  private _tryInitSession(): void {
    if (!this._disclaimerAccepted) {
      return;
    }
    if (!this.autoOpen && this._visibility !== 'expanded') {
      return;
    }
    void this._initSession();
  }

  /**
   * Mint a conversation and fetch chat history. Invoked via
   * {@link _tryInitSession} from {@link connectedCallback},
   * {@link _onAcceptDisclaimer}, and {@link _onOpen}.
   *
   * Bootstrap order:
   *   1. Show the loading indicator ({@link _sessionBootstrapping}).
   *   2. `POST {sessionEndpoint}`.
   *   3. `POST {historyEndpoint}`.
   *   4. Empty history → reveal the welcome bubble; non-empty history
   *      is logged to the console (transcript hydration is future work).
   *
   * Idempotent: a second invocation while a previous one is still
   * in flight returns the same `_sessionReady` promise; an
   * invocation after a successful mint short-circuits.
   *
   * Errors are logged-only here (no UI surface) — the user can
   * still type a message; the next `_streamMessage` call will
   * await `_sessionReady` and surface the failure as an error
   * bubble at the natural spot in the transcript.
   */
  private async _initSession(): Promise<void> {
    if (this._conversationId !== null) {
      return;
    }
    if (this._sessionReady !== null) {
      return this._sessionReady;
    }
    this._sessionBootstrapping = true;
    this._showWelcomeTurn = false;
    const apiClient = this._getApiClient();
    /* Capture the promise both as the field (so other awaits see
       the in-flight call) and as the return so the caller's await
       resolves at the same instant. Race-safe even if called twice
       in the same tick. */
    const ready = (async (): Promise<void> => {
      try {
        const session = await apiClient.startSession();
        this._conversationId = session.conversationId;
        await this._resolveBootstrapFromHistory(session.conversationId);
      } catch (err) {
        /* Reset so a retry path can try again. The next user
           submit will hit `_streamMessage`'s session-recovery
           branch and surface a proper error bubble. */
        this._sessionReady = null;
        this._sessionBootstrapping = false;
        this._showWelcomeTurn = true;
        /* eslint-disable-next-line no-console */
        console.warn('[mwi-assistant] startSession failed', err);
        throw err;
      }
    })();
    this._sessionReady = ready;
    /* Don't surface init errors to callers — they're already
       logged, and `_initSession` is a fire-and-forget seam. */
    return ready.catch(() => {
      /* swallowed — the next submit will re-mint via the same
         field-coalescing logic above */
    });
  }

  /**
   * After a conversation id is minted, fetch persisted history and
   * either reveal the welcome bubble (empty history) or hydrate the
   * transcript from the returned messages.
   */
  private async _resolveBootstrapFromHistory(
    conversationId: string
  ): Promise<void> {
    try {
      if (this.historyEndpoint.trim().length === 0) {
        this._showWelcomeTurn = true;
        return;
      }
      const history = await this._getApiClient().getChatHistory(
        conversationId
      );
      if (isChatHistoryEmpty(history)) {
        this._showWelcomeTurn = true;
      } else {
        const parsed = parseChatHistory(history, () =>
          this._generateTurnId()
        );
        this._turns = parsed.turns;
        this._showWelcomeTurn = false;
        /* Page navigation remounts the widget. Replay the end-of-chat
           card when history still ends on conversation-end, instead of
           leaving the raw marker in a bubble (WR3NCH-4022). */
        if (parsed.conversationEnded) {
          this._overallFeedbackActive = true;
        }
        this.updateComplete.then(() => this._scrollTranscriptToBottom());
      }
    } catch (err) {
      /* eslint-disable-next-line no-console */
      console.warn('[mwi-assistant] getChatHistory failed', err);
      this._showWelcomeTurn = true;
    } finally {
      this._sessionBootstrapping = false;
    }
  }

  /**
   * Open the single in-flight slot. Returns false when a request is
   * already running so the caller leaves the draft and the transcript
   * unchanged.
   */
  private _tryStartRequest(): boolean {
    if (this._generating) {
      return false;
    }
    this._activeGeneration += 1;
    this._generating = true;
    return true;
  }

  /**
   * Release the in-flight slot when `generation` is still the active
   * one. Stop, close, and detach bump {@link _activeGeneration} first,
   * so a late completion cannot re-enable Send out from under a newer
   * request or clear a flag they already cleared.
   */
  private _finishGeneration(generation: number): void {
    if (generation !== this._activeGeneration) {
      return;
    }
    this._generating = false;
  }

  /**
   * Stop the in-flight reply.
   *
   * Partial assistant text already on screen is kept and marked
   * finished: the streaming caret comes off, markdown renders, and
   * no error bubble is shown. If no text has arrived yet, the
   * thinking indicator is removed and the user message stays in the
   * thread. The input draft is not touched.
   */
  private _onStopGenerating(event: Event): void {
    event.preventDefault();
    if (!this._generating) {
      return;
    }
    this._activeGeneration += 1;
    this._finalizeStoppedAssistantTurn();
    this._abortInflightStream();
    this._generating = false;
    this.updateComplete.then(() => this._focusInput());
  }

  /**
   * Settle the assistant side of a stopped turn. See
   * {@link _onStopGenerating} for the keep-partial / drop-indicator
   * rule. Does not abort the fetch — the caller does that after the
   * turn id has been read.
   */
  private _finalizeStoppedAssistantTurn(): void {
    this._clearThinkingTimers();
    this._armOverallFeedbackAfterComplete = false;

    const streamingId = this._streamingTurnId;
    if (streamingId !== null) {
      const partial = this._turns.find((turn) => turn.id === streamingId);
      const { displayText } = parseConversationEndMessage(partial?.text ?? '');
      if (displayText.trim().length === 0) {
        this._turns = this._turns.filter((turn) => turn.id !== streamingId);
      } else {
        this._turns = this._turns.map((turn) =>
          turn.id === streamingId
            ? { ...turn, text: displayText, streaming: false }
            : turn
        );
      }
      this._streamingTurnId = null;
      this._thinkingTurnId = null;
      return;
    }

    this._removeThinkingTurn();
  }

  private _isStaleGeneration(
    generation: number,
    controller: AbortController | null
  ): boolean {
    return (
      generation !== this._activeGeneration ||
      controller === null ||
      this._inflightStreamAbort !== controller
    );
  }

  /**
   * Send a user message and receive the assistant reply (P1-058).
   * Uses {@link useStreaming}: when true, streams via
   * `POST /api/assistant/message/stream`; when false (default), awaits
   * `POST /api/assistant/message` JSON. Fire-and-forget from
   * {@link _onSubmit}, {@link _onStarterPromptClick},
   * {@link _onFollowUpPromptClick} (P1-058c), and
   * {@link _onRetryFromError}.
   *
   * Wraps these concerns:
   *  - **Session readiness.** Awaits {@link _sessionReady} so a fast
   *    typist on a slow network doesn't race the eager `/session`
   *    POST. If session init failed, we attempt a one-shot recovery
   *    (re-run `_initSession`) before giving up.
   *  - **Cancellation.** Owns the per-request `AbortController`
   *    stored on {@link _inflightStreamAbort}. Stop, close, and
   *    detach abort it. A second submit does not.
   *  - **State machine.** With streaming: iterates the api-client
   *    stream and feeds each event through the typed helpers
   *    ({@link _beginStreamingAssistantTurn},
   *    {@link _appendDeltaToStreamingTurn},
   *    {@link _completeStreamingTurn},
   *    {@link _errorStreamingTurn}). Without streaming: a single
   *    {@link AssistantApiClient.sendMessage} call then
   *    {@link _completeStreamingTurn} (or {@link _errorStreamingTurn}
   *    on failure).
   *
   * `clientContext` is the optional `parameters` payload from a
   * follow-up-prompt action chip (P1-058c). When present it is
   * forwarded verbatim on the message request body and
   * preserved on any error turn so a Retry can re-issue it.
   */
  private async _streamMessage(
    userText: string,
    clientContext?: Readonly<Record<string, string>>
  ): Promise<void> {
    const generation = this._activeGeneration;
    let controller: AbortController | null = null;
    try {
      /* Capture the conversation id for THIS request so a confirmed
         close (which nulls `_conversationId`) doesn't poison a
         previously-issued send. The corresponding AbortController
         takes care of actually stopping the in-flight fetch. */
      let conversationId = this._conversationId;
      if (conversationId === null && this._sessionReady === null) {
        /* Disclaimer-not-accepted shouldn't reach here (input is
           disabled), but if it does the safest move is to mint
           lazily so the message isn't dropped. */
        void this._initSession();
      }
      if (this._sessionReady !== null) {
        try {
          await this._sessionReady;
        } catch {
          /* Swallow — the catch below handles the no-id case. */
        }
        if (generation !== this._activeGeneration) {
          return;
        }
        conversationId = this._conversationId;
      }

      if (generation !== this._activeGeneration) {
        return;
      }

      if (conversationId === null) {
        /* No assistant message POST — without a conversation id the
           api-client cannot send. Missing id means session mint failed or
           the session JSON could not be parsed in api-client. */
        /* eslint-disable-next-line no-console */
        console.warn(
          '[mwi-assistant] not sending assistant message: no conversationId after session init'
        );
        /* Session init failed and a recovery attempt couldn't mint
           a new id. Surface as an error bubble so the user can hit
           Retry, which re-runs this whole pipeline (including a
           fresh `_initSession`). */
        this._errorStreamingTurn(
          'session_unavailable',
          'Could not start a conversation. Please try again.',
          userText,
          clientContext
        );
        return;
      }

      controller = new AbortController();
      this._inflightStreamAbort = controller;
      const apiClient = this._getApiClient();

      const request =
        clientContext === undefined
          ? { conversationId, message: userText }
          : { conversationId, message: userText, clientContext };
      if (this.useStreaming) {
        for await (const ev of apiClient.sendMessageStream(
          request,
          controller.signal
        )) {
          /* Stop, close, or detach bumps the generation and drops
             the controller. Ignore anything that arrives after that
             so a late complete/error cannot replace the settled turn. */
          if (this._isStaleGeneration(generation, controller)) {
            return;
          }
          switch (ev.type) {
            case 'delta': {
              if (this._streamingTurnId === null) {
                this._beginStreamingAssistantTurn(ev.textDelta);
              } else {
                this._appendDeltaToStreamingTurn(ev.textDelta);
              }
              break;
            }
            case 'complete': {
              this._completeStreamingTurn(
                ev.finalResponse.message,
                ev.finalResponse.actions
              );
              return;
            }
            case 'error': {
              this._errorStreamingTurn(
                ev.errorCode,
                ev.errorMessage,
                userText,
                clientContext
              );
              return;
            }
          }
        }
        /* The api-client always yields a terminal `complete` /
           `error` event; reaching here means the iteration was
           cancelled (abort) or the api-client surfaced a
           protocol_error already. Nothing to do. */
      } else {
        const finalResponse = await apiClient.sendMessage(
          request,
          controller.signal
        );
        if (this._isStaleGeneration(generation, controller)) {
          return;
        }
        this._completeStreamingTurn(
          finalResponse.message,
          finalResponse.actions
        );
      }
    } catch (err) {
      if (controller === null || this._isStaleGeneration(generation, controller)) {
        return;
      }
      if (
        controller.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError')
      ) {
        return;
      }
      if (err instanceof AssistantApiError) {
        this._errorStreamingTurn(
          err.errorCode,
          err.message,
          userText,
          clientContext
        );
        return;
      }
      this._errorStreamingTurn(
        'transport_error',
        DEFAULT_STREAM_ERROR_MESSAGE,
        userText,
        clientContext
      );
    } finally {
      if (controller !== null && this._inflightStreamAbort === controller) {
        this._inflightStreamAbort = null;
      }
      this._finishGeneration(generation);
    }
  }

  /**
   * Abort the in-flight message request, if any. Used by
   *  - Stop generating ({@link _onStopGenerating});
   *  - confirmed-close ({@link _endSession});
   *  - element detach ({@link disconnectedCallback}).
   *
   * Safe to call when no stream is open. Does NOT clear the
   * thinking turn or the streaming turn — the caller decides
   * whether the transcript should still show the bubble or wipe it.
   */
  private _abortInflightStream(): void {
    if (this._inflightStreamAbort !== null) {
      this._inflightStreamAbort.abort();
      this._inflightStreamAbort = null;
    }
    this._streamingTurnId = null;
  }

  /**
   * First `delta` of a stream landed: replace the thinking
   * indicator (or append a fresh turn if no indicator was open) with
   * a growing assistant bubble. Subsequent deltas hit
   * {@link _appendDeltaToStreamingTurn}.
   */
  private _beginStreamingAssistantTurn(initialText: string): void {
    this._clearThinkingTimers();

    const id = this._generateTurnId();
    const newTurn: MwiAssistantTurn = {
      id,
      role: 'assistant',
      text: initialText,
      timestamp: new Date(),
      streaming: true,
    };
    this._streamingTurnId = id;

    if (this._thinkingTurnId !== null) {
      const thinkingId = this._thinkingTurnId;
      this._thinkingTurnId = null;
      this._turns = this._turns.map((turn) =>
        turn.id === thinkingId ? newTurn : turn
      );
      return;
    }
    this._turns = [...this._turns, newTurn];
  }

  /**
   * Subsequent `delta` frames during an active stream: append the
   * incremental text to the trailing assistant turn. No-op if Stop
   * or teardown already cleared the streaming turn — the controller
   * was aborted, but one more frame may still arrive in the same tick.
   */
  private _appendDeltaToStreamingTurn(textDelta: string): void {
    const id = this._streamingTurnId;
    if (id === null) {
      return;
    }
    this._turns = this._turns.map((turn) =>
      turn.id === id ? { ...turn, text: turn.text + textDelta } : turn
    );
  }

  /**
   * Terminal `complete` frame landed. Finalises the streaming turn:
   * stores the server-supplied `actions[]` (P1-058a follow-up will
   * render them), clears the streaming caret, and uses the
   * authoritative final message text in case our delta-stitched
   * copy diverged. Also covers the rare path where `complete`
   * arrives before any `delta` (server emits no incremental
   * chunks).
   */
  private _completeStreamingTurn(
    finalMessage: string,
    actions: readonly AssistantAction[]
  ): void {
    this._clearThinkingTimers();

    const { displayText, isConversationEnd } =
      parseConversationEndMessage(finalMessage);
    const trimmedDisplay = displayText.trim();
    const armOverall =
      this._armOverallFeedbackAfterComplete || isConversationEnd;
    if (this._armOverallFeedbackAfterComplete) {
      this._armOverallFeedbackAfterComplete = false;
    }
    if (armOverall) {
      this._overallFeedbackActive = true;
    }

    /* Closure-done (and any other empty complete) must not leave a
       padded assistant bubble with no copy — overall feedback still
       renders below when armed. */
    if (trimmedDisplay.length === 0) {
      this._discardInFlightAssistantPlaceholder();
      return;
    }

    let id = this._streamingTurnId;
    if (id === null) {
      /* No deltas arrived — promote a brand-new turn directly
         from the complete frame. Replaces the thinking indicator
         in-place if one is still on screen. */
      id = this._generateTurnId();
      const newTurn: MwiAssistantTurn = {
        id,
        role: 'assistant',
        text: trimmedDisplay,
        timestamp: new Date(),
        actions,
        ...(armOverall ? { skipPerTurnFeedback: true } : {}),
      };
      if (this._thinkingTurnId !== null) {
        const thinkingId = this._thinkingTurnId;
        this._thinkingTurnId = null;
        this._turns = this._turns.map((turn) =>
          turn.id === thinkingId ? newTurn : turn
        );
      } else {
        this._turns = [...this._turns, newTurn];
      }
      return;
    }

    const finalisedId = id;
    this._streamingTurnId = null;
    this._turns = this._turns.map((turn) =>
      turn.id === finalisedId
        ? {
            ...turn,
            text: trimmedDisplay,
            streaming: false,
            actions,
            ...(armOverall ? { skipPerTurnFeedback: true } : {}),
          }
        : turn
    );
  }

  /**
   * Drop the thinking indicator and/or in-progress streaming bubble
   * without replacing them with a completed turn. Used when `complete`
   * has no user-visible prose (empty body or a `conversation-end`
   * marker with nothing around it).
   */
  private _discardInFlightAssistantPlaceholder(): void {
    const streamingId = this._streamingTurnId;
    const thinkingId = this._thinkingTurnId;
    this._streamingTurnId = null;
    this._thinkingTurnId = null;
    if (streamingId === null && thinkingId === null) {
      return;
    }
    this._turns = this._turns.filter(
      (turn) => turn.id !== streamingId && turn.id !== thinkingId
    );
  }

  /**
   * Terminal `error` event landed (server error frame OR synthetic
   * pre-stream error from the api-client). Replaces the streaming /
   * thinking turn with an error bubble that carries the originating
   * user message — the Retry button feeds it back into
   * {@link _streamMessage} via {@link _onRetryFromError} without the
   * user having to retype.
   *
   * `clientContext`, when present, is the `parameters` map that
   * accompanied the failing request (today only follow-up-prompt
   * chip clicks populate it — P1-058c). Stored alongside `userText`
   * so a retried follow-up-prompt re-issues the same context.
   *
   * Provisional copy + visual treatment, flagged for Liana's review
   * at the P4-001 Design QA gate. REQUIREMENTS.md §5 does not yet
   * spec error states.
   */
  private _errorStreamingTurn(
    code: string,
    message: string,
    userText: string,
    clientContext?: Readonly<Record<string, string>>
  ): void {
    this._clearThinkingTimers();

    const safeMessage =
      message.length > 0 ? message : DEFAULT_STREAM_ERROR_MESSAGE;
    const error: MwiAssistantTurn['error'] =
      clientContext === undefined
        ? { code, message: safeMessage, userText }
        : { code, message: safeMessage, userText, clientContext };
    const errorTurn: MwiAssistantTurn = {
      id: this._generateTurnId(),
      role: 'assistant',
      text: '',
      timestamp: new Date(),
      error,
    };

    if (this._streamingTurnId !== null) {
      const streamingId = this._streamingTurnId;
      this._streamingTurnId = null;
      this._turns = this._turns.map((turn) =>
        turn.id === streamingId ? errorTurn : turn
      );
      return;
    }
    if (this._thinkingTurnId !== null) {
      const thinkingId = this._thinkingTurnId;
      this._thinkingTurnId = null;
      this._turns = this._turns.map((turn) =>
        turn.id === thinkingId ? errorTurn : turn
      );
      return;
    }
    this._turns = [...this._turns, errorTurn];
  }

  /**
   * Retry click handler on an error bubble. Drops the error turn,
   * re-arms the thinking indicator, and re-runs the user message
   * through the same pipeline as a fresh submit, without appending
   * another user bubble (the original is already in the transcript).
   * Ignored while another request is in flight.
   *
   * If a new request was started in between (e.g. the user submitted
   * another message before clicking Retry on the older error), this
   * is a no-op — the error turn we'd be retrying is no longer in
   * `_turns` and removing it would be silent confusion.
   */
  private _onRetryFromError(turn: MwiAssistantTurn): void {
    if (turn.error === undefined) {
      return;
    }
    if (!this._tryStartRequest()) {
      return;
    }
    const userText = turn.error.userText;
    const clientContext = turn.error.clientContext;
    const errorId = turn.id;
    /* Drop the error bubble in-place; `_startThinking` will
       append a fresh thinking turn at the tail of the transcript. */
    this._turns = this._turns.filter((t) => t.id !== errorId);
    this._startThinking();
    void this._streamMessage(userText, clientContext);
  }

  private _focusInput(): void {
    const input =
      this.shadowRoot?.querySelector<HTMLTextAreaElement>('.input');
    input?.focus();
  }

  /**
   * Placeholder copy for the message input. Swaps the moment the first
   * user turn lands in the transcript per REQUIREMENTS.md §5.1:
   *
   *  - Initial (before first message): "Type your question here…"
   *  - After first message sent:        "Add your reply here…"
   *
   * Read from the rendered textarea via a `?placeholder=${...}` binding
   * in {@link renderFooter} — Lit re-evaluates this getter on every
   * render, which fires when `_turns` mutates after a successful send.
   */
  private get _inputPlaceholder(): string {
    return this._hasUserTurn
      ? 'Add your reply here…'
      : 'Type your question here…';
  }

  private _scrollTranscriptToBottom(): void {
    const body = this.shadowRoot?.querySelector<HTMLElement>('.body');
    if (body) {
      body.scrollTop = body.scrollHeight;
    }
  }

  override render(): TemplateResult | typeof nothing {
    if (this._visibility !== 'expanded') {
      return this.renderLauncher();
    }
    const showDisclaimer = !this._disclaimerAccepted;
    /* `inert` blocks pointer + keyboard interaction with the chat
       surface whenever *any* overlay is showing — the disclaimer
       (P1-051), the close-confirmation modal (P1-056), and the
       post-feedback thank-you overlay (P3-024c) all qualify.
       We deliberately do not nest modals: the disclaimer must be
       accepted before the others are reachable, and close-confirm
       vs thank-you are mutually exclusive in practice. */
    const showCloseConfirm = this._closeConfirmOpen && !showDisclaimer;
    const showFeedbackThankYou =
      this._feedbackThankYouOpen && !showDisclaimer && !showCloseConfirm;
    const overlayShowing =
      showDisclaimer || showCloseConfirm || showFeedbackThankYou;
    return html`
      <section
        class="frame"
        part="frame"
        role="region"
        aria-label="Ask MWI"
      >
        <div class="frame__content" ?inert=${overlayShowing}>
          ${this.renderHeader()} ${this.renderBody()} ${this.renderFooter()}
        </div>
        ${showDisclaimer ? this.renderDisclaimer() : nothing}
        ${showCloseConfirm ? this.renderCloseConfirm() : nothing}
        ${showFeedbackThankYou ? this.renderFeedbackThankYou() : nothing}
      </section>
    `;
  }

  /**
   * Floating launcher shown when the widget is minimized or closed
   * (P1-055). The aria-label distinguishes the two states so screen
   * readers convey "you have an open conversation" vs "start a new one".
   *
   * Provisional visual treatment — the production launcher is owned by
   * P4-006 (nav-bar integration) and P4-007 (pulsing animation when a
   * session is active). For Phase 1 this floats in the same bottom-right
   * spot as the frame so minimize/close are reachable end-to-end.
   */
  private renderLauncher(): TemplateResult | typeof nothing {
    if (!this.launcherVisible) {
      return nothing;
    }
    const sessionActive = this._visibility === 'minimized';
    const label = sessionActive
      ? 'Open Ask MWI (session in progress)'
      : 'Open Ask MWI';
    return html`
      <button
        type="button"
        class="launcher"
        part="launcher"
        aria-label=${label}
        aria-expanded="false"
        @click=${this._onOpen}
      >
        ${this.renderAiSparkIcon()}
      </button>
    `;
  }

  private renderHeader(): TemplateResult {
    return html`
      <header class="header" part="header">
        <div
          class="header__drag-handle"
          title="Drag to resize or reposition"
          @pointerdown=${this._onHeaderBrandPointerDown}
          @pointermove=${this._onHeaderBrandPointerMove}
          @pointerup=${this._onHeaderBrandPointerUp}
          @pointercancel=${this._onHeaderBrandPointerUp}
        >
          <div class="header__brand">
            <span class="header__avatar" aria-hidden="true">
              ${this.renderAiSparkIcon()}
            </span>
            <h2 class="header__title">Ask MWI</h2>
          </div>
        </div>
        <div class="header__actions">
          <button
            type="button"
            class="header__icon-button"
            part="minimize-button"
            aria-label="Minimize chat"
            @click=${this._onMinimize}
          >
            ${this.renderMinimizeIcon()}
          </button>
          <button
            type="button"
            class="header__icon-button"
            part="close-button"
            aria-label="Close chat"
            @click=${this._onClose}
          >
            ${this.renderCloseIcon()}
          </button>
        </div>
      </header>
    `;
  }

  private renderBody(): TemplateResult {
    /* role="log" + aria-live="polite" makes new assistant/user turns
       announced by screen readers as they're appended. The static welcome
       bubble is initial content, not announced — that's the correct
       behavior. The transcript region is the scroll container so newly
       appended turns are guaranteed to land below the welcome bubble
       with consistent vertical rhythm. */
    return html`
      <div
        class="body"
        part="body"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label="Conversation transcript"
      >
        ${this._sessionBootstrapping
          ? this.renderBootstrapLoadingTurn()
          : nothing}
        ${this._showWelcomeTurn ? this.renderWelcomeTurn() : nothing}
        ${this._turns.map((turn) => this.renderTurn(turn))}
        ${this._overallFeedbackActive
          ? this._renderOverallFeedback()
          : nothing}
      </div>
    `;
  }

  private renderBootstrapLoadingTurn(): TemplateResult {
    return html`
      <div class="turn turn--assistant" part="turn-bootstrap-loading">
        <p class="timestamp">${this.formatTimestamp(new Date())}</p>
        <div
          class="bubble bubble--thinking"
          part="bubble-bootstrap-loading"
          role="status"
          aria-live="polite"
        >
          ${this.renderThinkingDots()}
          <span class="sr-only">${BOOTSTRAP_LOADING_SR_LABEL}</span>
        </div>
      </div>
    `;
  }

  private renderWelcomeTurn(): TemplateResult {
    /* The chip rail is disabled while the disclaimer is up (defence in
       depth alongside the inert chat surface) and again — permanently —
       after the first user turn lands. Once a conversation has started
       the starter prompts are no longer relevant, so re-enabling them
       would just be clutter. See {@link _chipsDisabled}. */
    const chipsDisabled = this._chipsDisabled;
    return html`
      <div class="turn turn--assistant" part="turn-welcome">
        <p class="timestamp">${this.formatTimestamp(new Date())}</p>
        <div class="bubble" part="bubble">
          <div class="bubble__text">
            <p class="bubble__heading">Helpful when you need it. Human when you want it.</p>
            <p class="bubble__paragraph">Ask MWI gives you another way to find answers and navigate your orders online. It's not here to replace your MWI team.</p>
            <p class="bubble__paragraph">Here are some examples of what Ask MWI can help you with:</p>
          </div>
          <ul class="chips" part="chips">
            ${DEFAULT_STARTER_PROMPTS.map(
              (chip) => html`
                <li>
                  <button
                    type="button"
                    class="chip"
                    part="chip"
                    ?disabled=${chipsDisabled}
                    @click=${() => this._onStarterPromptClick(chip)}
                  >
                    ${chip.label}
                  </button>
                </li>
              `
            )}
          </ul>
        </div>
      </div>
    `;
  }

  private renderTurn(turn: MwiAssistantTurn): TemplateResult {
    if (turn.role === 'user') {
      return html`
        <div class="turn turn--user" part="turn-user">
          <p class="timestamp">${this.formatTimestamp(turn.timestamp)}</p>
          <div class="bubble bubble--user" part="bubble-user">
            <p class="bubble__text-only">${turn.text}</p>
          </div>
        </div>
      `;
    }

    if (turn.thinking !== undefined) {
      return this.renderThinkingTurn(turn);
    }

    if (turn.error !== undefined) {
      return this.renderErrorTurn(turn);
    }

    const streamingClass = turn.streaming === true ? ' bubble--streaming' : '';
    const part =
      turn.streaming === true
        ? 'bubble-assistant bubble-assistant-streaming'
        : 'bubble-assistant';
    /* The feedback row (P3-023a) is rendered as a sibling of the
       bubble (NOT inside it) so its 16px top gap, 24px gutter, and
       intrinsic content width sit cleanly below the bubble per
       Figma `Chatbot_postive feedback on response` (`5642:4530`).
       Hidden while the bubble is still streaming so users don't
       rate a half-finished response — once `_completeStreamingTurn`
       drops the streaming flag, the row appears on the next render.

       The negative-reason card (P3-025a) mounts as a third sibling
       inside the same turn wrapper when the user has clicked
       thumbs-down and the follow-up has not yet resolved
       (`turn.feedbackPending === true`). Wired in P3-023b-1; the
       card itself is unchanged. The mount/unmount/dispatch flow
       lives entirely in the host so each turn's card is its own
       per-turn instance with its own listener closures. */
    return html`
      <div class="turn turn--assistant" part="turn-assistant">
        <p class="timestamp">${this.formatTimestamp(turn.timestamp)}</p>
        <div class="bubble${streamingClass}" part=${part}>
          ${turn.streaming === true
            ? html`<p class="bubble__text-only">${turn.text}</p>`
            : renderAssistantMarkdownBody(turn.text)}
          ${this._renderActions(turn)}
        </div>
        ${turn.streaming === true ? nothing : this._renderFeedbackRow(turn)}
        ${turn.feedbackPending === true
          ? this._renderNegativeReasonCard(turn)
          : nothing}
      </div>
    `;
  }

  /**
   * Mount the {@link MwiFeedbackReasonCardElement} primitive
   * (P3-025a) for a turn whose thumb-down click has not yet
   * resolved. Per-turn instance — the listeners are closed over
   * `this`, while the originating turn is identified through the
   * card's `turn-id` attribute so the per-turn handlers can route
   * the event back to the right transcript entry without per-card
   * bookkeeping (P3-023b-1).
   *
   * The default `title-copy` (per-turn variant) is intentionally
   * not overridden — the end-of-chat caller (P3-024a) is the one
   * that supplies the longer `did not meet your expectations`
   * wording. The card primitive's exposed `::part` hooks remain
   * the source of truth for any host-page styling.
   */
  private _renderNegativeReasonCard(turn: MwiAssistantTurn): TemplateResult {
    /* Event names are pinned literally below because Lit's
       `@<name>=${handler}` binding requires the name to be a
       static template token — it cannot be an interpolated
       expression, so we cannot import the FEEDBACK_REASONS_EVENT /
       FEEDBACK_DISMISS_EVENT constants here. The literals match
       the strings exported from `feedback-reason-card.ts`; if
       either constant ever drifts the binding will silently
       no-op, which is what the per-turn tests in
       `mwi-assistant.test.ts` are there to catch. */
    return html`
      <mwi-feedback-reason-card
        part="feedback-reason-card"
        turn-id=${turn.id}
        @mwi-assistant-feedback-reasons=${this._onFeedbackReasons}
        @mwi-assistant-feedback-dismiss=${this._onFeedbackDismiss}
      ></mwi-feedback-reason-card>
    `;
  }

  /**
   * Render the per-turn feedback row (P3-023a) — `Helpful response?`
   * prompt + thumb-up / thumb-down buttons — as a sibling of the
   * assistant bubble inside the turn wrapper. The Figma source
   * frames (Dev-Handoff `DSMVDudtzt2Haob8uI37xQ`):
   *
   *  - `Chatbot_postive feedback on response` (`5642:4454` /
   *    `5642:4530`) — canonical pre/post-click states.
   *  - `Chatbot_feedback hover` (`5642:3677`) — hover treatment
   *    (Open extraction, captured in TOKENS.md).
   *  - `Chatbot_neg feedback_followup` (`5642:4606`) — context
   *    for how this row sits ABOVE the negative-reason inline
   *    card from P3-025a (parallel agent, separate PR).
   *
   * Behavioural contract is sticky-once-clicked: after a click on
   * either thumb, the prompt swaps to `Thanks for the feedback!`,
   * the chosen icon flips its `aria-pressed` to `"true"` (the CSS
   * `[aria-pressed="true"]` selector swaps to the filled-purple
   * treatment), and both buttons go inert (`disabled` +
   * `aria-disabled="true"` via the disabled HTML attribute, which
   * happy-dom and every browser surface as `aria-disabled`).
   *
   * NOT rendered for the welcome turn (the welcome bubble is in
   * `renderWelcomeTurn()` — separate render branch — so this method
   * never sees it). NOT rendered for user turns (gated by the
   * `role === 'user'` early return in {@link renderTurn}). NOT
   * rendered while a turn is still streaming (gated at the call
   * site so users don't rate a half-finished response). NOT
   * rendered for thinking-indicator or error turns (their separate
   * render branches don't call this method). The intent is "only
   * substantive completed assistant content responses carry a
   * feedback row" — matches the Figma reference frames.
   *
   * Click semantics: see {@link _onFeedbackClick}.
   */
  private _renderFeedbackRow(turn: MwiAssistantTurn): TemplateResult {
    if (turn.skipPerTurnFeedback === true) {
      return html``;
    }
    const submitted = turn.feedbackSubmitted;
    const hasSubmitted = submitted !== undefined;
    const promptText = hasSubmitted
      ? FEEDBACK_PROMPT_SUBMITTED
      : FEEDBACK_PROMPT_INITIAL;
    const upPressed = submitted === 'positive';
    const downPressed = submitted === 'negative';
    return html`
      <div
        class="feedback-row"
        part="feedback-row"
        role="group"
        aria-label="Feedback on assistant response"
      >
        <span class="feedback-prompt" part="feedback-prompt"
          >${promptText}</span
        >
        <button
          type="button"
          class="feedback-thumb"
          part="feedback-thumb-up"
          aria-label="Helpful"
          aria-pressed=${upPressed ? 'true' : 'false'}
          ?disabled=${hasSubmitted}
          @click=${() => this._onFeedbackClick(turn, 'positive')}
        >
          ${this.renderThumbIcon('up')}
        </button>
        <button
          type="button"
          class="feedback-thumb"
          part="feedback-thumb-down"
          aria-label="Not helpful"
          aria-pressed=${downPressed ? 'true' : 'false'}
          ?disabled=${hasSubmitted}
          @click=${() => this._onFeedbackClick(turn, 'negative')}
        >
          ${this.renderThumbIcon('down')}
        </button>
      </div>
    `;
  }

  /**
   * Render the **overall-experience** feedback card (P3-024a) at the
   * bottom of the transcript after the user taps **"No, I'm done"**
   * and the closure thank-you assistant reply lands. Hidden again
   * when they send a later message (restart); it returns only after
   * the next conversation-end.
   *
   * **Not** the per-turn {@link _renderFeedbackRow} (`Helpful response?`
   * beneath every substantive reply — P3-023a). Copy, placement, wire
   * shape (`kind: 'overall'` / `type: end-of-chat`), and trigger are
   * all distinct. Reuses the same thumb icon primitive only.
   */
  private _renderOverallFeedback(): TemplateResult {
    const submitted = this._overallFeedbackSubmitted;
    const hasSubmitted = submitted !== undefined;
    const promptText = hasSubmitted
      ? FEEDBACK_PROMPT_SUBMITTED
      : OVERALL_FEEDBACK_PROMPT;
    const upPressed = submitted === 'positive';
    const downPressed = submitted === 'negative';
    return html`
      <div
        class="overall-feedback"
        part="overall-feedback"
        role="group"
        aria-label="Overall feedback on Ask MWI"
      >
        <p class="overall-feedback__title" part="overall-feedback-title">
          ${OVERALL_FEEDBACK_TITLE}
        </p>
        <div class="overall-feedback__row" part="overall-feedback-row">
          <span class="feedback-prompt" part="overall-feedback-prompt"
            >${promptText}</span
          >
          <button
            type="button"
            class="feedback-thumb"
            part="overall-feedback-thumb-up"
            aria-label="Positive overall experience"
            aria-pressed=${upPressed ? 'true' : 'false'}
            ?disabled=${hasSubmitted}
            @click=${() => this._onOverallFeedbackClick('positive')}
          >
            ${this.renderThumbIcon('up')}
          </button>
          <button
            type="button"
            class="feedback-thumb"
            part="overall-feedback-thumb-down"
            aria-label="Negative overall experience"
            aria-pressed=${downPressed ? 'true' : 'false'}
            ?disabled=${hasSubmitted}
            @click=${() => this._onOverallFeedbackClick('negative')}
          >
            ${this.renderThumbIcon('down')}
          </button>
        </div>
        ${this._overallFeedbackPending
          ? html`
              <mwi-feedback-reason-card
                part="overall-feedback-reason-card"
                turn-id=${OVERALL_FEEDBACK_CORRELATION_ID}
                title-copy=${OVERALL_FEEDBACK_REASON_TITLE}
                @mwi-assistant-feedback-reasons=${this._onFeedbackReasons}
                @mwi-assistant-feedback-dismiss=${this._onFeedbackDismiss}
              ></mwi-feedback-reason-card>
            `
          : nothing}
      </div>
    `;
  }

  /**
   * Render the suggested-actions chip row below the assistant
   * message text (P1-058a + P1-058c). Lives **inside** the same
   * bubble div as the prose so the bubble's
   * `gap: var(--mwi-assistant-space-4)` (16px) gives the row
   * natural top spacing — matches the Figma reference frames
   * `Chatbot_reply with product info` (Dev-Handoff
   * `DSMVDudtzt2Haob8uI37xQ`, node `5642:4378`) for `link-out`
   * and `Chatbot_need more help` (`5642:4208`) for
   * `follow-up-prompt`.
   *
   * Render branches today:
   *  - `link-out` (P1-058a) — outlined `<a>` chip that opens
   *    `url` in a new tab.
   *  - `follow-up-prompt` (P1-058c) — outlined `<button>` chip
   *    whose click submits `label` as the next user message
   *    through the same submission pathway as a typed input
   *    (see {@link _onFollowUpPromptClick}). The whole bubble's
   *    follow-up-prompt chips disable on the first click via
   *    the per-turn `actionsConsumed` flag.
   *  - `intent` — typed but **not rendered in-bubble**. Reserved
   *    for the server-driven welcome rail (P1-067).
   *
   * Streaming-friendly: the function is called every render, but
   * the renderable subset is empty (`actions` is `undefined`)
   * while the bubble is still being grown by `delta` frames —
   * `_completeStreamingTurn` is what stamps the `actions` field
   * the moment the terminal frame lands. So the chip row appears
   * exactly when the assistant finishes speaking.
   *
   * If the turn has no actions, or all of its actions are
   * non-renderable (only `intent` types today), returns
   * `nothing` so no empty `<ul>` appears in the bubble — keeps
   * the assistant's first-turn shell visually identical to the
   * streaming bubble it grew from.
   *
   * **Speculative emission warning.** As of 2026-04-29 the agent
   * team has not committed to *emitting* `follow-up-prompt`
   * actions on the wire (TOKENS.md Open question #13). The
   * render path lands today so the wire change is a one-side
   * server flip, validated end-to-end via the dev-harness mock-
   * response panel (`index.html` "Mock responses" section,
   * P1-058b). If the contract shifts before the agent team
   * adopts it, this branch is the only widget-side change.
   */
  private _renderActions(turn: MwiAssistantTurn): TemplateResult | typeof nothing {
    const actions = turn.actions;
    if (actions === undefined || actions.length === 0) {
      return nothing;
    }
    /* Filter to renderable kinds in source order. `intent`
       actions are typed but reserved for the welcome rail
       (P1-067) — they are silently skipped here. Source-order
       preservation lets the server interleave chip kinds (a
       `link-out` to a tracker followed by a `follow-up-prompt`
       to ask about returns, e.g. the `Chatbot_representative
       help` frame) without the widget reordering them. */
    const visible = actions.filter(
      (a): a is LinkOutAction | FollowUpPromptAction =>
        a.type === 'link-out' || a.type === 'follow-up-prompt'
    );
    if (visible.length === 0) {
      return nothing;
    }
    /* Per-bubble one-shot gate — once any follow-up-prompt chip
       in this turn is clicked, every follow-up-prompt chip in
       the same row disables. `link-out` chips ignore the flag.
       See {@link MwiAssistantTurn.actionsConsumed} for the
       rationale. */
    const consumed = turn.actionsConsumed === true || this._generating;
    const turnId = turn.id;
    return html`
      <ul class="chips chips--actions" part="chips actions">
        ${visible.map((action) =>
          action.type === 'link-out'
            ? this._renderLinkOutChip(action)
            : this._renderFollowUpPromptChip(action, turnId, consumed)
        )}
      </ul>
    `;
  }

  private _renderLinkOutChip(action: LinkOutAction): TemplateResult {
    return html`
      <li>
        <a
          class="chip"
          part="chip chip-link-out"
          data-action-type="link-out"
          href=${action.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          ${action.label}
        </a>
      </li>
    `;
  }

  /**
   * Render one `follow-up-prompt` chip (P1-058c). The chip is a
   * real `<button type="button">` rather than an `<a>` because
   * clicking submits a message rather than navigating. Same
   * `.chip` class as `link-out` chips and the welcome-bubble
   * starter chips so all three share one visual primitive — the
   * Figma `Button responses` token block (`5642:4429` /
   * `5642:4430` in the Dev-Handoff file). Hover/focus/active
   * states are owed to Liana's interaction-states extraction
   * (TOKENS.md Open extraction); reduced-motion has no effect
   * today because the chip has no animation.
   *
   * `?disabled=${consumed}` flips on the first chip click in the
   * same bubble. The per-turn flag (set in
   * {@link _onFollowUpPromptClick}) survives a re-render
   * triggered by the subsequent user-bubble append + thinking
   * indicator, so the disabled state is stable across the
   * streaming round-trip.
   */
  private _renderFollowUpPromptChip(
    action: FollowUpPromptAction,
    originTurnId: string,
    consumed: boolean
  ): TemplateResult {
    return html`
      <li>
        <button
          type="button"
          class="chip"
          part="chip chip-follow-up-prompt"
          data-action-type="follow-up-prompt"
          ?disabled=${consumed}
          @click=${() => this._onFollowUpPromptClick(action, originTurnId)}
        >
          ${action.label}
        </button>
      </li>
    `;
  }

  /**
   * Renders an error turn — the bubble that lands when an SSE
   * stream terminates in an `error` event (P1-058). Provisional
   * treatment + copy, flagged for Liana's review at P4-001
   * (REQUIREMENTS.md §5 does not yet spec error states).
   *
   * The Retry button feeds `turn.error.userText` back into
   * {@link _streamMessage} via {@link _onRetryFromError}, so the
   * user doesn't have to retype.
   */
  private renderErrorTurn(turn: MwiAssistantTurn): TemplateResult {
    const error = turn.error;
    if (error === undefined) {
      return html``;
    }
    return html`
      <div class="turn turn--assistant" part="turn-error">
        <p class="timestamp">${this.formatTimestamp(turn.timestamp)}</p>
        <div
          class="bubble bubble--error"
          part="bubble-assistant bubble-assistant-error"
          role="alert"
        >
          <p class="bubble__text-only">${error.message}</p>
          <button
            type="button"
            class="error-retry"
            part="error-retry"
            ?disabled=${this._generating}
            @click=${() => this._onRetryFromError(turn)}
          >
            ${RETRY_BUTTON_LABEL}
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Renders the thinking indicator (P1-054). Two phases share the same
   * bubble shell so the swap doesn't shift the surrounding layout —
   * only the inner content changes:
   *
   *  - `'dots'`: three pulsing dots (aria-hidden so the visuals don't
   *     interfere) plus a visually-hidden "Assistant is thinking" line
   *     that the polite live region reads on append.
   *  - `'text'`: a random playful line from {@link THINKING_TEXT_MESSAGES}
   *     (stored on the turn as `thinkingText`) with the same pulsing
   *     dots as phase 1 to its right. The inner `role="status"`
   *     text-content change is announced by AT; the dots are decorative
   *     (`aria-hidden`).
   *
   * Stable ::part hooks (`turn-thinking`, `bubble-thinking`,
   * `bubble-thinking-dots` / `bubble-thinking-text`) let host CSS theme
   * the indicator independently of regular bubbles. The thinking turn
   * is removed automatically by {@link _appendAssistantTurn} when the
   * real reply lands, so render-side cleanup is implicit.
   */
  private renderThinkingTurn(turn: MwiAssistantTurn): TemplateResult {
    const phase = turn.thinking ?? 'dots';
    const part =
      phase === 'dots'
        ? 'bubble-thinking bubble-thinking-dots'
        : 'bubble-thinking bubble-thinking-text';
    const inner =
      phase === 'dots'
        ? html`
            ${this.renderThinkingDots()}
            <span class="sr-only">${THINKING_DOTS_SR_LABEL}</span>
          `
        : html`
            <div class="thinking-text">
              <p class="bubble__text-only thinking-text__label">
                ${turn.thinkingText ?? THINKING_TEXT_MESSAGES[0]}
              </p>
              ${this.renderThinkingDots()}
            </div>
          `;
    return html`
      <div class="turn turn--assistant" part="turn-thinking">
        <p class="timestamp">${this.formatTimestamp(turn.timestamp)}</p>
        <div
          class="bubble bubble--thinking"
          part=${part}
          role="status"
          aria-live="polite"
        >
          ${inner}
        </div>
      </div>
    `;
  }

  /**
   * Renders the legal disclaimer modal that gates first interaction
   * (P1-051). The modal is positioned absolutely inside the frame so the
   * widget retains its bottom-right docking; the chat surface beneath is
   * marked `inert` to prevent both pointer and keyboard interaction. Uses
   * `role="alertdialog"` because the user MUST act before continuing.
   */
  /**
   * Renders the close-confirmation alertdialog (P1-056). Same modal
   * idiom as the disclaimer: absolutely positioned inside the frame,
   * scrim backdrop via `--mwi-assistant-color-backdrop`, chat surface
   * marked `inert`. `role="alertdialog"` because the user MUST act
   * (Cancel or End conversation) before continuing — there is no
   * implicit dismissal beyond Esc, which routes through Cancel.
   *
   * Cancel comes first in tab order *and* receives initial focus so
   * the destructive action is never the default — see
   * {@link _focusCloseConfirmCancel}. The Esc key also routes to
   * Cancel via {@link _onCloseConfirmKeydown}.
   */
  /**
   * Renders the post–end-of-chat thank-you overlay (P3-024c). Same
   * modal idiom as {@link renderCloseConfirm}: scrim + card, chat
   * surface `inert`. Non-interactive — no actions; auto-dismisses
   * via {@link _showFeedbackThankYouAndScheduleClose}.
   */
  private renderFeedbackThankYou(): TemplateResult {
    return html`
      <div
        class="confirm"
        part="feedback-thank-you"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="mwi-assistant-feedback-thank-you-title"
      >
        <div class="confirm__card" part="feedback-thank-you-card">
          <h3
            id="mwi-assistant-feedback-thank-you-title"
            class="confirm__title"
          >
            ${FEEDBACK_THANK_YOU_TITLE}
          </h3>
        </div>
      </div>
    `;
  }

  private renderCloseConfirm(): TemplateResult {
    return html`
      <div
        class="confirm"
        part="close-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="mwi-assistant-close-confirm-title"
        aria-describedby="mwi-assistant-close-confirm-body"
        @keydown=${this._onCloseConfirmKeydown}
      >
        <div class="confirm__card" part="close-confirm-card">
          <h3
            id="mwi-assistant-close-confirm-title"
            class="confirm__title"
          >
            ${CLOSE_CONFIRM_TITLE}
          </h3>
          <p
            id="mwi-assistant-close-confirm-body"
            class="confirm__body"
          >
            ${CLOSE_CONFIRM_BODY}
          </p>
          <div class="confirm__actions">
            <button
              type="button"
              class="confirm__button confirm__button--secondary"
              part="close-confirm-cancel"
              @click=${this._onCancelClose}
            >
              ${CLOSE_CONFIRM_CANCEL_LABEL}
            </button>
            <button
              type="button"
              class="confirm__button confirm__button--primary"
              part="close-confirm-confirm"
              @click=${this._onConfirmClose}
            >
              ${CLOSE_CONFIRM_CONFIRM_LABEL}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderDisclaimer(): TemplateResult {
    const text = this.disclaimerText || DEFAULT_DISCLAIMER_TEXT;
    return html`
      <div
        class="disclaimer"
        part="disclaimer"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="mwi-assistant-disclaimer-title"
        aria-describedby="mwi-assistant-disclaimer-body"
      >
        <div class="disclaimer__card" part="disclaimer-card">
          <h3
            id="mwi-assistant-disclaimer-title"
            class="disclaimer__title"
          >
            Before we begin
          </h3>
          <p
            id="mwi-assistant-disclaimer-body"
            class="disclaimer__body"
          >
            ${text}
          </p>
          <div class="disclaimer__actions">
            <button
              type="button"
              class="disclaimer__accept"
              part="disclaimer-accept"
              @click=${this._onAcceptDisclaimer}
            >
              I Agree
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderFooter(): TemplateResult {
    /* Input stays editable while a reply is in flight so the user can
       draft the next message. Send is disabled until that request
       finishes or is stopped, and also when the draft is empty. */
    const interactive = this._disclaimerAccepted;
    const hasContent = this._inputValue.trim().length > 0;
    const sendDisabled = !interactive || !hasContent || this._generating;
    return html`
      <footer class="footer" part="footer">
        <form
          class="input-row input-row--form"
          part="input-row"
          @submit=${this._onSubmit}
          novalidate
        >
          <textarea
            class="input"
            part="input"
            rows="1"
            placeholder=${this._inputPlaceholder}
            aria-label="Type your question"
            autocomplete="off"
            .value=${this._inputValue}
            ?disabled=${!interactive}
            @input=${this._onInput}
            @keydown=${this._onInputKeydown}
          ></textarea>
          ${this._generating
            ? html`
                <button
                  type="button"
                  class="stop"
                  part="stop-button"
                  aria-label="Stop generating"
                  @click=${this._onStopGenerating}
                >
                  ${this.renderStopIcon()}
                </button>
              `
            : nothing}
          <button
            type="submit"
            class="send"
            part="send-button"
            aria-label="Send message"
            ?disabled=${sendDisabled}
          >
            ${this.renderSendIcon()}
          </button>
        </form>
      </footer>
    `;
  }

  /**
   * "Today at H:MM AM/PM" — matches the Figma timestamp format.
   * Locale-aware via Intl; falls back to a 12-hour US format if Intl is
   * unavailable in the runtime (defensive, since the widget targets evergreen
   * browsers).
   */
  private formatTimestamp(date: Date): string {
    try {
      const time = new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      }).format(date);
      return `Today at ${time}`;
    } catch {
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const meridiem = hours >= 12 ? 'PM' : 'AM';
      const displayHour = hours % 12 || 12;
      return `Today at ${displayHour}:${minutes} ${meridiem}`;
    }
  }

  /**
   * Header avatar / launcher glyph — chat-bubble + sparkle composition
   * supplied by Jake as a cleaner replacement for the prior hand-rolled
   * approximation of the Figma `Ai-Chat-Spark--Streamline-Ultimate` glyph.
   * Stroke is bound to `currentColor` so the icon inherits the host
   * surface's text color: white-on-navy in the header
   * (`--mwi-assistant-color-text-on-primary`) and white-on-purple in the
   * launcher button — same wiring the minimize/close icons use, no
   * per-icon color overrides required. Jake's source had a `<clipPath>`
   * against the 24×24 viewBox; dropped here because the path geometry is
   * fully contained inside the viewBox already, so the clip was a no-op
   * that only added an `id="a"` that could collide if the widget were
   * ever rendered twice on a page.
   *
   * The `<g transform="translate(2.75 2.7)">` wrapper recenters the glyph
   * within the viewBox. Jake's raw path has its bounding box at roughly
   * x ≈ 0.93–17.58 and y ≈ 1.13–17.5, which puts the visual center near
   * (9.26, 9.31) — visibly offset to the upper-left when dropped into
   * containers that center the SVG element (the round launcher button is
   * the most obvious offender, but the header avatar drifts too). Shifting
   * by (+2.75, +2.7) lands the bounding-box center on (12, 12), the
   * viewBox center, so the icon reads as visually centered on every host
   * surface without needing surface-specific positioning hacks. Kept as a
   * group transform rather than baked into the path so the source path
   * stays diff-friendly if Jake hands us an updated version later.
   */
  /** Three pulsing dots shared by both thinking-indicator phases (P1-054). */
  private renderThinkingDots(): TemplateResult {
    return html`
      <span class="thinking-dots" aria-hidden="true">
        <span class="thinking-dot"></span>
        <span class="thinking-dot"></span>
        <span class="thinking-dot"></span>
      </span>
    `;
  }

  private renderAiSparkIcon(): TemplateResult {
    return html`
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <g transform="translate(2.75 2.7)">
          <path
            d="M11.2952 4.33032c1.5657-.31477 2.8264-1.59074 3.1417-3.19702.3152 1.60628 1.5756 2.88225 3.1413 3.19702m0 .00183c-1.5657.31477-2.8264 1.59075-3.1416 3.19702-.3153-1.60627-1.5757-2.88225-3.1414-3.19702M8.33107 3.30811H2.47412c-.85189 0-1.542479.69059-1.542479 1.54248V12.828c0 .8519.690589 1.5425 1.542479 1.5425h1.68102v2.5705c0 .7033.86417 1.0396 1.33957.5214l2.60714-2.842c.14608-.1593.35223-.2499.56833-.2499h5.41392c.8519 0 1.5424-.6906 1.5424-1.5425V9.89676"
          />
        </g>
      </svg>
    `;
  }

  private renderMinimizeIcon(): TemplateResult {
    return html`
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        aria-hidden="true"
      >
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    `;
  }

  private renderCloseIcon(): TemplateResult {
    return html`
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        aria-hidden="true"
      >
        <line x1="6" y1="6" x2="18" y2="18" />
        <line x1="18" y1="6" x2="6" y2="18" />
      </svg>
    `;
  }

  private renderSendIcon(): TemplateResult {
    return html`
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m22 2-7 20-4-9-9-4Z" />
        <path d="M22 2 11 13" />
      </svg>
    `;
  }

  private renderStopIcon(): TemplateResult {
    return html`
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" />
      </svg>
    `;
  }

  /**
   * Per-turn feedback thumbs glyph (P3-023a). Lucide `thumbs-up` is
   * a permissive free analog to the Figma icon (TOKENS.md tracks the
   * exact source as an Open extraction). The thumb-down variant is
   * the same path mirrored in both axes (`scaleY(-1) scaleX(-1)`)
   * so a Liana-supplied final asset only needs to land in one place.
   *
   * Stroke = currentColor + fill = none in the resting state; the
   * `[aria-pressed="true"]` selector in the static styles flips
   * `fill` (and `stroke`) to `--mwi-assistant-color-interactive-primary`
   * for the selected/sticky state.
   */
  private renderThumbIcon(direction: 'up' | 'down'): TemplateResult {
    const transform =
      direction === 'down' ? 'translate(24 24) scale(-1 -1)' : '';
    return html`
      <svg
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <g transform=${transform}>
          <path
            d="M7 10v12"
          />
          <path
            d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"
          />
        </g>
      </svg>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mwi-assistant': MwiAssistantElement;
  }
}
